import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus.ts';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { useClickOutside } from '../hooks/useClickOutside.ts';
import * as XLSX from 'xlsx';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCopy,
  FaUserPlus,
  FaCode,
  FaTimes,
  FaCheck,
  FaLock,
  FaChartBar,
  FaThLarge,
  FaBookOpen,
  FaUsers,
  FaCog,
  FaGem,
  FaQuestionCircle,
  FaChevronRight,
  FaSearch,
  FaGraduationCap,
  FaGripVertical,
  FaChevronDown,
  FaDownload,
  FaEye,
  FaClock,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaLink,
  FaUserClock,
  FaPencilAlt,
  FaCommentDots,
} from 'react-icons/fa';
import { API_BASE } from '../utils/apiEndpoints';
import { problemPath } from '../utils/problemUrls.ts';
import './css/ClassEditPage.css';

interface ProblemRef {
  id: number;
  problem_id: number;
  order: number;
  problem: {
    id: number;
    title: string;
    difficulty: string;
    topic?: string;
    slug?: string | null;
    unlisted?: boolean;
  } | null;
}

interface Lesson {
  id: number;
  title: string;
  order: number;
  is_published?: boolean;
  due_at?: string | null;
  problems: ProblemRef[];
}

interface Member {
  id: number;
  user_id: number;
  role: string;
  username: string | null;
  avatar: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  joined_at?: string | null;
  solved_count?: number;
  last_activity_at?: string | null;
}

interface ClassData {
  id: number;
  name: string;
  invite_code: string;
  created_at: string;
  updated_at: string;
  can_edit?: boolean;
  total_problems_in_class?: number;
  lessons: Lesson[];
  members: Member[];
  teacher_classes?: { id: number; name: string }[];
}

interface ProblemOption {
  id: number;
  title: string;
  difficulty: string;
  topic?: string;
}

interface AnalyticsProblem {
  problemId: number;
  title: string;
}

interface AnalyticsStudent {
  userId: number;
  username: string | null;
  avatar?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

interface AnalyticsSubmission {
  id?: number;
  userId: number;
  problemId: number;
  status: string;
  submitted_at: string;
  execution_time?: number | null;
  language: string;
  code: string;
  feedback?: {
    status: 'unread' | 'read' | 'replied';
    unread_reply: boolean;
  } | null;
}

interface LessonAnalytics {
  lesson: { id: number; title: string };
  problems: AnalyticsProblem[];
  students: AnalyticsStudent[];
  submissions: AnalyticsSubmission[];
}

interface ClassActivityItem {
  type: 'submission';
  at: string | null;
  userId: number;
  username: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  problemId: number;
  problemTitle: string;
  status: string;
}

type ClassTab = 'overview' | 'assignment' | 'customProblems' | 'students' | 'analytics' | 'settings';

function localTodayYmd() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function pickCurrentAssignment(lessons: Lesson[] | undefined, today: string): Lesson | null {
  const published = (lessons || []).filter((l) => l.is_published !== false);
  const usable = published.filter((l) => (l.problems || []).length > 0);
  const pool = usable.length ? usable : published;
  if (!pool.length) return null;
  const withDue = pool.filter((l) => l.due_at);
  const upcoming = withDue
    .filter((l) => String(l.due_at) >= today)
    .sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)));
  if (upcoming.length) return upcoming[0];
  const overdue = withDue
    .filter((l) => String(l.due_at) < today)
    .sort((a, b) => String(b.due_at).localeCompare(String(a.due_at)));
  if (overdue.length) return overdue[0];
  return [...pool].sort((a, b) => b.order - a.order || b.id - a.id)[0];
}

function ClassEditPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: classIdFromRoute } = useParams<{ id: string }>();
  const { t, currentLanguage } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const canAccessClassPage = user?.role === 'admin' || user?.role === 'teacher';
  const uiLang = (currentLanguage || 'en').substring(0, 2).toLowerCase();
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [createClassLoading, setCreateClassLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [addingLesson, setAddingLesson] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
  const [editingLessonTitle, setEditingLessonTitle] = useState('');
  const [addProblemLessonId, setAddProblemLessonId] = useState<number | null>(null);
  const [problemsSearch, setProblemsSearch] = useState('');
  const [problemsOptions, setProblemsOptions] = useState<ProblemOption[]>([]);
  const [problemsSearchLoading, setProblemsSearchLoading] = useState(false);
  const [problemsTopic, setProblemsTopic] = useState('All');
  const [problemsDifficulty, setProblemsDifficulty] = useState('All');
  const [problemsSource, setProblemsSource] = useState<'catalog' | 'custom'>('catalog');
  const [problemsPage, setProblemsPage] = useState(1);
  const [problemsTotalPages, setProblemsTotalPages] = useState(1);
  const [problemTopics, setProblemTopics] = useState<string[]>([]);
  const [addingProblemId, setAddingProblemId] = useState<number | null>(null);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState(false);
  const [deleteLessonModal, setDeleteLessonModal] = useState<{ id: number; title: string } | null>(null);
  const [removeProblemModal, setRemoveProblemModal] = useState<{
    lessonId: number;
    problemId: number;
    title: string;
  } | null>(null);
  const tabFromUrl = searchParams.get('tab');
  const initialTab: ClassTab =
    tabFromUrl === 'assignment' ||
    tabFromUrl === 'customProblems' ||
    tabFromUrl === 'students' ||
    tabFromUrl === 'analytics' ||
    tabFromUrl === 'settings' ||
    tabFromUrl === 'overview'
      ? tabFromUrl
      : 'overview';
  const [activeTab, setActiveTab] = useState<ClassTab>(initialTab);
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(new Set());
  const [programSearch, setProgramSearch] = useState('');
  const [selectedLessonIdForAnalytics, setSelectedLessonIdForAnalytics] = useState<number | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [lessonAnalytics, setLessonAnalytics] = useState<LessonAnalytics | null>(null);
  const [analyticsModalSubmission, setAnalyticsModalSubmission] = useState<AnalyticsSubmission | null>(null);
  const [analyticsModalHistory, setAnalyticsModalHistory] = useState<AnalyticsSubmission[]>([]);
  const [analyticsModalHistoryLoading, setAnalyticsModalHistoryLoading] = useState(false);
  const [analyticsCommentDraft, setAnalyticsCommentDraft] = useState('');
  const [analyticsComments, setAnalyticsComments] = useState<{
    id: number;
    body: string;
    createdAt: string;
    author_role?: string;
    read_at?: string | null;
  }[]>([]);
  const [analyticsCommentsLoading, setAnalyticsCommentsLoading] = useState(false);
  const [analyticsCommentSending, setAnalyticsCommentSending] = useState(false);
  const [analyticsCommentError, setAnalyticsCommentError] = useState<string | null>(null);
  const [analyticsCommentSent, setAnalyticsCommentSent] = useState(false);
  const [removeMemberModal, setRemoveMemberModal] = useState<{ userId: number; username: string } | null>(null);
  const [dragProblem, setDragProblem] = useState<{ lessonId: number; problemId: number } | null>(null);
  const [dragOverProblemId, setDragOverProblemId] = useState<number | null>(null);
  const [dragLessonId, setDragLessonId] = useState<number | null>(null);
  const [dragOverLessonId, setDragOverLessonId] = useState<number | null>(null);
  const [analyticsStudentFilter, setAnalyticsStudentFilter] = useState<number[]>([]);
  const [classActivity, setClassActivity] = useState<ClassActivityItem[]>([]);
  const [classActivityLoading, setClassActivityLoading] = useState(false);
  const [analyticsStudentMenuOpen, setAnalyticsStudentMenuOpen] = useState(false);
  const [analyticsStudentSearch, setAnalyticsStudentSearch] = useState('');
  const [analyticsPeriod, setAnalyticsPeriod] = useState<'all' | '7d' | '30d'>('all');
  const analyticsStudentMenuRef = useRef<HTMLDivElement>(null);
  const [studentsSearch, setStudentsSearch] = useState('');
  const [studentsStatusFilter, setStudentsStatusFilter] = useState<'all' | 'active' | 'inactive' | 'at_risk' | 'missing'>('all');
  const [assignmentProgress, setAssignmentProgress] = useState<{
    lessonId: number;
    missingUserIds: number[];
    notStartedUserIds: number[];
    solvedUserIds: number[];
    problemCount: number;
  } | null>(null);
  const [studentsSort, setStudentsSort] = useState<'activity' | 'progress' | 'name'>('activity');
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsExportMenuOpen, setStudentsExportMenuOpen] = useState(false);
  const studentsExportMenuRef = useRef<HTMLDivElement>(null);
  const studentsPageSize = 10;
  const [customProblemsSearch, setCustomProblemsSearch] = useState('');
  const [customProblemsList, setCustomProblemsList] = useState<{
    id: number;
    title: string;
    difficulty: string;
    topic?: string;
    slug?: string | null;
    lessons: { id: number; title: string }[];
  }[]>([]);
  const [customProblemsLoading, setCustomProblemsLoading] = useState(false);
  const [customProblemsTotal, setCustomProblemsTotal] = useState(0);
  const [classSelectOpen, setClassSelectOpen] = useState(false);
  const classSelectRef = useRef<HTMLDivElement>(null);
  const [createClassModalOpen, setCreateClassModalOpen] = useState(false);
  const [createClassModalName, setCreateClassModalName] = useState('');
  const [deleteClassModalOpen, setDeleteClassModalOpen] = useState(false);
  const [deleteClassConfirmName, setDeleteClassConfirmName] = useState('');
  const [deleteClassLoading, setDeleteClassLoading] = useState(false);
  const [customProblemModal, setCustomProblemModal] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const closeStudentsExportMenu = useCallback(() => setStudentsExportMenuOpen(false), []);
  const closeClassSelect = useCallback(() => setClassSelectOpen(false), []);
  useClickOutside(studentsExportMenuRef, closeStudentsExportMenu);
  useClickOutside(classSelectRef, closeClassSelect);

  const fetchMyClass = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/classes/mine?lang=${encodeURIComponent(uiLang)}`, { credentials: 'include' });
      if (res.status === 404) {
        setClassData(null);
        setCreateMode(true);
        return;
      }
      if (!res.ok) throw new Error(t('classEdit.loadClassError'));
      const data = await res.json();
      setClassData(data);
      setEditName(data.name);
      setCreateMode(false);
      setSelectedLessonIdForAnalytics(null);
      setLessonAnalytics(null);
      setExpandedLessons(data.lessons?.[0]?.id ? new Set([data.lessons[0].id]) : new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
      setClassData(null);
    } finally {
      setLoading(false);
    }
  }, [t, uiLang]);

  const fetchClassById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/classes/${id}?lang=${encodeURIComponent(uiLang)}`, { credentials: 'include' });
      if (res.status === 404) {
        setError(t('classEdit.classNotFound'));
        setClassData(null);
        return;
      }
      if (res.status === 403) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || t('classEdit.noAccessToClass'));
        setClassData(null);
        return;
      }
      if (!res.ok) throw new Error(t('classEdit.loadClassError'));
      const data = await res.json();
      setClassData(data);
      setEditName(data.name);
      setCreateMode(false);
      setSelectedLessonIdForAnalytics(null);
      setLessonAnalytics(null);
      setExpandedLessons(data.lessons?.[0]?.id ? new Set([data.lessons[0].id]) : new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
      setClassData(null);
    } finally {
      setLoading(false);
    }
  }, [t, uiLang]);

  const refetchCurrentClass = useCallback(() => {
    if (classIdFromRoute) return fetchClassById(classIdFromRoute);
    return fetchMyClass();
  }, [classIdFromRoute, fetchClassById, fetchMyClass]);

  const fetchLessonAnalytics = useCallback(async (lessonId: number) => {
    if (!classData) return;
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      setLessonAnalytics(null);
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}/analytics?lang=${encodeURIComponent(uiLang)}`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAnalyticsError(data.message || t('classEdit.analyticsLoadError'));
        return;
      }
      setLessonAnalytics(data as LessonAnalytics);
    } catch (e) {
      setAnalyticsError(e instanceof Error ? e.message : t('classEdit.analyticsLoadError'));
    } finally {
      setAnalyticsLoading(false);
    }
  }, [classData, t, uiLang]);

  const isTeacher = !!classData && !!user && classData.members.some(
    (m) => Number(m.user_id) === Number(user.id) && m.role === 'teacher'
  );
  const canEdit = isTeacher && (classData?.can_edit !== false);

  const fetchClassActivity = useCallback(async (id: number) => {
    setClassActivityLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${id}/activity?lang=${encodeURIComponent(uiLang)}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        setClassActivity([]);
        return;
      }
      const data = await res.json();
      setClassActivity(Array.isArray(data.items) ? data.items : []);
    } catch {
      setClassActivity([]);
    } finally {
      setClassActivityLoading(false);
    }
  }, [uiLang]);

  useEffect(() => {
    if (!classData?.id || !canEdit) {
      setClassActivity([]);
      return;
    }
    fetchClassActivity(classData.id);
  }, [classData?.id, canEdit, fetchClassActivity]);

  const fetchAssignmentProgress = useCallback(async (classId: number, lessonId?: number) => {
    try {
      const params = new URLSearchParams({ today: localTodayYmd() });
      if (lessonId) params.set('lessonId', String(lessonId));
      const res = await fetch(
        `${API_BASE}/api/classes/${classId}/assignment-progress?${params}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        setAssignmentProgress(null);
        return;
      }
      const data = await res.json();
      if (!data.lesson) {
        setAssignmentProgress(null);
        return;
      }
      setAssignmentProgress({
        lessonId: Number(data.lesson.id),
        missingUserIds: Array.isArray(data.missing_user_ids) ? data.missing_user_ids.map(Number) : [],
        notStartedUserIds: Array.isArray(data.not_started_user_ids) ? data.not_started_user_ids.map(Number) : [],
        solvedUserIds: Array.isArray(data.solved_user_ids) ? data.solved_user_ids.map(Number) : [],
        problemCount: Number(data.lesson.problem_count) || 0,
      });
    } catch {
      setAssignmentProgress(null);
    }
  }, []);

  useEffect(() => {
    if (!classData?.id || !canEdit) {
      setAssignmentProgress(null);
      return;
    }
    void fetchAssignmentProgress(classData.id);
  }, [classData?.id, canEdit, fetchAssignmentProgress]);

  useEffect(() => {
    if (!classData?.id || !isTeacher) return;
    void fetch(`${API_BASE}/api/notifications/read-class-submissions`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ class_id: classData.id }),
    });
  }, [classData?.id, isTeacher]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin' && user?.role !== 'teacher') {
      navigate('/', { replace: true });
      return;
    }
    if (classIdFromRoute) {
      fetchClassById(classIdFromRoute);
    } else {
      fetchMyClass();
    }
  }, [authLoading, isAuthenticated, user, navigate, classIdFromRoute, fetchMyClass, fetchClassById]);

  useEffect(() => {
    if (!canEdit || !classData) return;
    if (activeTab !== 'analytics') return;
    let lessonId = selectedLessonIdForAnalytics;
    if (!lessonId && classData.lessons.length > 0) {
      lessonId = pickCurrentAssignment(classData.lessons, localTodayYmd())?.id
        || classData.lessons.find((l) => l.is_published !== false)?.id
        || classData.lessons[0].id;
      setSelectedLessonIdForAnalytics(lessonId);
    }
    if (lessonId) {
      fetchLessonAnalytics(lessonId);
    }
  }, [activeTab, canEdit, classData, selectedLessonIdForAnalytics, fetchLessonAnalytics]);

  const handleCreateClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasActiveSubscription) {
      navigate('/subscription');
      return;
    }
    const name = (createClassModalOpen ? createClassModalName : newClassName).trim();
    if (!name) return;
    setCreateClassLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/classes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('classEdit.errorGeneric'));
      setError(null);
      setNewClassName('');
      setCreateClassModalOpen(false);
      setCreateClassModalName('');
      setCreateMode(false);
      navigate(`/class/${data.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setCreateClassLoading(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!classData) return;
    if (deleteClassConfirmName.trim() !== classData.name) {
      setError(t('classEdit.deleteClassNameMismatch'));
      return;
    }
    setDeleteClassLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm_name: deleteClassConfirmName.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || t('classEdit.errorGeneric'));
      setDeleteClassModalOpen(false);
      setDeleteClassConfirmName('');
      const remaining = Array.isArray(data.remaining) ? data.remaining : [];
      if (remaining.length > 0) {
        navigate(`/class/${remaining[0].id}`);
      } else {
        setClassData(null);
        setCreateMode(true);
        navigate('/my-class', { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setDeleteClassLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!classData || editName.trim() === classData.name) return;
    setSavingName(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) => {
        if (!prev) return null;
        const name = editName.trim();
        return {
          ...prev,
          name,
          teacher_classes: (prev.teacher_classes || []).map((c) =>
            c.id === prev.id ? { ...c, name } : c,
          ),
        };
      });
      setEditingName(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setSavingName(false);
    }
  };

  const handleAddLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!classData || !newLessonTitle.trim()) return;
    setAddingLesson(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/lessons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: newLessonTitle.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(data.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) =>
        prev
          ? {
              ...prev,
              lessons: [...prev.lessons, {
                id: data.id,
                title: data.title,
                order: data.order,
                is_published: Boolean(data.is_published),
                due_at: data.due_at ?? null,
                problems: data.problems || [],
              }],
            }
          : null
      );
      setExpandedLessons((prev) => new Set([...prev, data.id]));
      setNewLessonTitle('');
      setActiveTab('overview');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setAddingLesson(false);
    }
  };

  const handleUpdateLesson = async (lessonId: number) => {
    if (!classData || !editingLessonTitle.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: editingLessonTitle.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) =>
        prev
          ? {
              ...prev,
              lessons: prev.lessons.map((l) =>
                l.id === lessonId ? { ...l, title: editingLessonTitle.trim() } : l
              ),
            }
          : null
      );
      setEditingLessonId(null);
      setEditingLessonTitle('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handlePatchLesson = async (
    lessonId: number,
    patch: { is_published?: boolean; due_at?: string | null },
  ) => {
    if (!classData) return;
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) =>
        prev
          ? {
              ...prev,
              lessons: prev.lessons.map((l) =>
                l.id === lessonId
                  ? {
                      ...l,
                      is_published: typeof d.is_published === 'boolean' ? d.is_published : l.is_published,
                      due_at: Object.prototype.hasOwnProperty.call(d, 'due_at') ? d.due_at : l.due_at,
                    }
                  : l
              ),
            }
          : null
      );
      void fetchAssignmentProgress(classData.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handleDeleteLesson = async (lessonId: number) => {
    if (!classData) return;
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) =>
        prev ? { ...prev, lessons: prev.lessons.filter((l) => l.id !== lessonId) } : null
      );
      setDeleteLessonModal(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const closeAddProblemModal = useCallback(() => {
    setAddProblemLessonId(null);
    setProblemsSearch('');
    setProblemsOptions([]);
    setProblemsTopic('All');
    setProblemsDifficulty('All');
    setProblemsSource('catalog');
    setProblemsPage(1);
    setProblemsTotalPages(1);
    setAddingProblemId(null);
  }, []);

  const openAddProblemModal = (lessonId: number) => {
    setProblemsSearch('');
    setProblemsTopic('All');
    setProblemsDifficulty('All');
    setProblemsSource('catalog');
    setProblemsPage(1);
    setProblemsOptions([]);
    setAddingProblemId(null);
    setAddProblemLessonId(lessonId);
  };

  const fetchAddProblemList = useCallback(async (
    search: string,
    topic: string,
    difficulty: string,
    page: number,
    source: 'catalog' | 'custom',
    classId: number,
  ) => {
    setProblemsSearchLoading(true);
    try {
      if (source === 'custom') {
        const params = new URLSearchParams({
          page: String(page),
          limit: '15',
          lang: uiLang,
        });
        if (search.trim()) params.set('search', search.trim());
        if (topic && topic !== 'All') params.set('topic', topic);
        if (difficulty && difficulty !== 'All') params.set('difficulty', difficulty);
        const res = await fetch(
          `${API_BASE}/api/classes/${classId}/custom-problems?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          setProblemsOptions([]);
          setProblemsTotalPages(1);
          return;
        }
        const data = await res.json();
        setProblemsOptions(
          (data.problems || []).map((p: ProblemOption) => ({
            id: p.id,
            title: p.title,
            difficulty: p.difficulty,
            topic: p.topic,
          })),
        );
        const totalPages = Number(data.totalPages) || Math.max(1, Math.ceil((Number(data.total) || 0) / 15) || 1);
        setProblemsTotalPages(totalPages);
        return;
      }

      const params = new URLSearchParams({
        page: String(page),
        lang: uiLang,
      });
      if (search.trim()) params.set('search', search.trim());
      if (topic && topic !== 'All') params.set('topic', topic);
      if (difficulty && difficulty !== 'All') params.set('difficulty', difficulty);
      const res = await fetch(`${API_BASE}/api/problems/problems_get?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setProblemsOptions([]);
        setProblemsTotalPages(1);
        return;
      }
      const data = await res.json();
      setProblemsOptions(
        (data.problems || []).map((p: ProblemOption) => ({
          id: p.id,
          title: p.title,
          difficulty: p.difficulty,
          topic: p.topic,
        }))
      );
      const totalPages = Number(data.totalPages) || Math.max(1, Math.ceil((Number(data.total) || 0) / 15) || 1);
      setProblemsTotalPages(totalPages);
    } catch {
      setProblemsOptions([]);
      setProblemsTotalPages(1);
    } finally {
      setProblemsSearchLoading(false);
    }
  }, [uiLang]);

  useEffect(() => {
    if (addProblemLessonId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/problems/topics_and_tags`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.topics)) {
          setProblemTopics(data.topics.filter((t: unknown): t is string => typeof t === 'string' && t.trim().length > 0));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [addProblemLessonId]);

  useEffect(() => {
    if (addProblemLessonId == null || !classData) return;
    const timer = setTimeout(() => {
      fetchAddProblemList(
        problemsSearch,
        problemsTopic,
        problemsDifficulty,
        problemsPage,
        problemsSource,
        classData.id,
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [
    addProblemLessonId,
    classData,
    problemsSearch,
    problemsTopic,
    problemsDifficulty,
    problemsPage,
    problemsSource,
    fetchAddProblemList,
  ]);

  const handleAddProblemToLesson = async (lessonId: number, problemId: number) => {
    if (!classData) return;
    setAddingProblemId(problemId);
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}/problems`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ problem_id: problemId }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(data.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          lessons: prev.lessons.map((l) => {
            if (l.id !== lessonId) return l;
            return {
              ...l,
              problems: [...l.problems, data],
            };
          }),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setAddingProblemId(null);
    }
  };

  const handleRemoveProblemFromLesson = async (lessonId: number, problemId: number) => {
    if (!classData) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}/problems/${problemId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          lessons: prev.lessons.map((l) => {
            if (l.id !== lessonId) return l;
            return { ...l, problems: l.problems.filter((p) => p.problem_id !== problemId) };
          }),
        };
      });
      setRemoveProblemModal(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handleDeleteCustomProblem = async (problemId: number) => {
    if (!classData) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/custom-problems/${problemId}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(data.message || t('classEdit.errorGeneric'));
      }
      setCustomProblemsList((prev) => prev.filter((p) => p.id !== problemId));
      setCustomProblemsTotal((prev) => Math.max(0, prev - 1));
      setClassData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          lessons: prev.lessons.map((l) => ({
            ...l,
            problems: l.problems.filter((pr) => pr.problem_id !== problemId),
          })),
        };
      });
      setCustomProblemModal(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handleReorderLessons = async (orderedLessonIds: number[]) => {
    if (!classData) return;
    const prevLessons = classData.lessons;
    const byId = new Map(prevLessons.map((l) => [l.id, l]));
    const nextLessons = orderedLessonIds
      .map((lid, idx) => {
        const item = byId.get(lid);
        if (!item) return null;
        return { ...item, order: idx + 1 };
      })
      .filter(Boolean) as Lesson[];

    setClassData((prev) => (prev ? { ...prev, lessons: nextLessons } : null));

    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/lessons/reorder`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ lesson_ids: orderedLessonIds }),
        },
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
    } catch (e) {
      setClassData((prev) => (prev ? { ...prev, lessons: prevLessons } : null));
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handleReorderProblems = async (lessonId: number, orderedProblemIds: number[]) => {
    if (!classData) return;
    const lesson = classData.lessons.find((l) => l.id === lessonId);
    if (!lesson) return;

    const prevProblems = lesson.problems;
    const byId = new Map(prevProblems.map((p) => [p.problem_id, p]));
    const nextProblems = orderedProblemIds
      .map((pid, idx) => {
        const item = byId.get(pid);
        if (!item) return null;
        return { ...item, order: idx + 1 };
      })
      .filter(Boolean) as ProblemRef[];

    setClassData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        lessons: prev.lessons.map((l) =>
          l.id === lessonId ? { ...l, problems: nextProblems } : l
        ),
      };
    });

    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/lessons/${lessonId}/problems/reorder`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ problem_ids: orderedProblemIds }),
        }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
    } catch (e) {
      setClassData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          lessons: prev.lessons.map((l) =>
            l.id === lessonId ? { ...l, problems: prevProblems } : l
          ),
        };
      });
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!classData || !inviteInput.trim()) return;
    setInviteLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/${classData.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: inviteInput.trim(), email: inviteInput.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(data.message || t('classEdit.errorGeneric'));
      }
      setInviteInput('');
      setInviteSuccess(data.message || t('classEdit.inviteSent'));
      setTimeout(() => setInviteSuccess(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!classData) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/members/${userId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) await refetchCurrentClass();
        throw new Error(d.message || t('classEdit.errorGeneric'));
      }
      setClassData((prev) =>
        prev ? { ...prev, members: prev.members.filter((m) => m.user_id !== userId) } : null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('classEdit.errorGeneric'));
    }
  };

  const copyInviteLink = () => {
    if (!classData?.invite_code) return;
    const url = `${window.location.origin}/join-class?code=${classData.invite_code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2000);
    });
  };

  const getDifficultyColor = (d: string) => {
    if (d === 'Easy') return 'var(--nn-difficulty-easy)';
    if (d === 'Medium') return 'var(--nn-difficulty-medium)';
    return 'var(--nn-difficulty-hard)';
  };

  const getDifficultyLabel = (d: string) => {
    if (d === 'Easy') return t('classEdit.diffEasy');
    if (d === 'Medium') return t('classEdit.diffMedium');
    if (d === 'Hard') return t('classEdit.diffHard');
    return d;
  };

  const toggleLesson = (lessonId: number) => {
    setExpandedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  useCloseOnEscape(!!removeMemberModal, () => setRemoveMemberModal(null));
  useCloseOnEscape(!!deleteLessonModal, () => setDeleteLessonModal(null));
  useCloseOnEscape(!!removeProblemModal, () => setRemoveProblemModal(null));
  useCloseOnEscape(!!customProblemModal, () => setCustomProblemModal(null));
  useCloseOnEscape(addProblemLessonId != null, closeAddProblemModal);
  useCloseOnEscape(!!analyticsModalSubmission, () => {
    setAnalyticsModalSubmission(null);
    setAnalyticsModalHistory([]);
  });
  useCloseOnEscape(analyticsStudentMenuOpen, () => setAnalyticsStudentMenuOpen(false));
  useCloseOnEscape(studentsExportMenuOpen, closeStudentsExportMenu);
  useCloseOnEscape(classSelectOpen, closeClassSelect);
  useCloseOnEscape(createClassModalOpen, () => setCreateClassModalOpen(false));
  useCloseOnEscape(deleteClassModalOpen, () => {
    setDeleteClassModalOpen(false);
    setDeleteClassConfirmName('');
  });

  useEffect(() => {
    if (!analyticsStudentMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!analyticsStudentMenuRef.current?.contains(e.target as Node)) {
        setAnalyticsStudentMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [analyticsStudentMenuOpen]);

  const analyticsPeriodStart = useMemo(() => {
    if (analyticsPeriod === 'all') return null;
    const d = new Date();
    d.setDate(d.getDate() - (analyticsPeriod === '7d' ? 7 : 30));
    return d;
  }, [analyticsPeriod]);

  const submissionInPeriod = useCallback(
    (sub: AnalyticsSubmission | undefined) => {
      if (!sub) return false;
      if (!analyticsPeriodStart) return true;
      return new Date(sub.submitted_at) >= analyticsPeriodStart;
    },
    [analyticsPeriodStart]
  );

  const getAnalyticsStatus = (status?: string) => {
    if (!status) return 'empty' as const;
    if (status === 'Accepted') return 'solved' as const;
    if (status === 'Pending') return 'review' as const;
    return 'error' as const;
  };

  const filteredAnalyticsStudents = useMemo(() => {
    if (!lessonAnalytics) return [];
    let list = lessonAnalytics.students;
    if (analyticsStudentFilter.length > 0) {
      const selected = new Set(analyticsStudentFilter);
      list = list.filter((s) => selected.has(s.userId));
    }
    const q = analyticsStudentSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const uname = (s.username || '').toLowerCase();
        const full = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
        return uname.includes(q) || full.includes(q);
      });
    }
    return list;
  }, [lessonAnalytics, analyticsStudentFilter, analyticsStudentSearch]);

  const analyticsStats = useMemo(() => {
    if (!lessonAnalytics) {
      return { progress: 0, active: 0, totalStudents: 0, solved: 0, totalCells: 0, review: 0, errors: 0 };
    }
    const students = filteredAnalyticsStudents;
    const problems = lessonAnalytics.problems;
    const totalStudents = students.length;
    const totalCells = totalStudents * problems.length;
    let solved = 0;
    let review = 0;
    let errors = 0;
    const activeIds = new Set<number>();

    for (const s of students) {
      for (const p of problems) {
        const sub = lessonAnalytics.submissions.find(
          (x) => x.userId === s.userId && x.problemId === p.problemId
        );
        if (!sub || !submissionInPeriod(sub)) continue;
        activeIds.add(s.userId);
        const st = getAnalyticsStatus(sub.status);
        if (st === 'solved') solved += 1;
        else if (st === 'review') review += 1;
        else if (st === 'error') errors += 1;
      }
    }

    const progress = totalCells > 0 ? Math.round((solved / totalCells) * 100) : 0;
    return {
      progress,
      active: activeIds.size,
      totalStudents,
      solved,
      totalCells,
      review,
      errors,
    };
  }, [lessonAnalytics, filteredAnalyticsStudents, submissionInPeriod]);

  const problemSolveRates = useMemo(() => {
    if (!lessonAnalytics) return new Map<number, number>();
    const map = new Map<number, number>();
    const n = filteredAnalyticsStudents.length || 1;
    for (const p of lessonAnalytics.problems) {
      let solved = 0;
      for (const s of filteredAnalyticsStudents) {
        const sub = lessonAnalytics.submissions.find(
          (x) => x.userId === s.userId && x.problemId === p.problemId
        );
        if (sub && submissionInPeriod(sub) && sub.status === 'Accepted') solved += 1;
      }
      map.set(p.problemId, Math.round((solved / n) * 100));
    }
    return map;
  }, [lessonAnalytics, filteredAnalyticsStudents, submissionInPeriod]);

  const studentDisplayName = (s: AnalyticsStudent) => {
    const full = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
    return full || s.username || `ID ${s.userId}`;
  };

  const studentShortName = (s: AnalyticsStudent) => {
    const first = (s.firstName || '').trim();
    const last = (s.lastName || '').trim();
    if (first && last) return `${first} ${last.charAt(0)}.`;
    if (first) return first;
    return s.username || `ID ${s.userId}`;
  };

  const studentInitials = (s: AnalyticsStudent) => {
    const a = (s.firstName || '').trim();
    const b = (s.lastName || '').trim();
    if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || '?';
    return (s.username || '?').slice(0, 2).toUpperCase();
  };

  const analyticsStatusLabel = (status: 'solved' | 'review' | 'error' | 'empty') => {
    if (status === 'solved') return t('classEdit.solved');
    if (status === 'review') return t('classEdit.statusReview');
    if (status === 'error') return t('classEdit.statusError');
    return t('classEdit.statusNotStarted');
  };

  const submissionStatusLabel = (status: string) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'accepted' || normalized === 'solved') return t('classEdit.solved');
    if (normalized === 'review' || normalized === 'pending') return t('classEdit.statusReview');
    if (normalized.includes('error') || normalized === 'wrong answer' || normalized === 'wrong_answer') {
      return t('classEdit.statusError');
    }
    return status || '—';
  };

  const closeAnalyticsCodeModal = () => {
    setAnalyticsModalSubmission(null);
    setAnalyticsModalHistory([]);
    setAnalyticsModalHistoryLoading(false);
    setAnalyticsCommentDraft('');
    setAnalyticsComments([]);
    setAnalyticsCommentsLoading(false);
    setAnalyticsCommentSending(false);
    setAnalyticsCommentError(null);
    setAnalyticsCommentSent(false);
  };

  const openAnalyticsCodeModal = async (sub: AnalyticsSubmission) => {
    if (!classData || !selectedLessonIdForAnalytics) return;
    setAnalyticsModalSubmission(sub);
    setAnalyticsModalHistory([sub]);
    setAnalyticsModalHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        userId: String(sub.userId),
        problemId: String(sub.problemId),
      });
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/lessons/${selectedLessonIdForAnalytics}/analytics/submissions?${params}`,
        { credentials: 'include' },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !Array.isArray(data.submissions) || data.submissions.length === 0) {
        return;
      }
      const list = data.submissions as AnalyticsSubmission[];
      setAnalyticsModalHistory(list);
      const preferred =
        (sub.id != null ? list.find((item) => item.id === sub.id) : null) ||
        list.find((item) => item.submitted_at === sub.submitted_at) ||
        list[0];
      if (preferred) setAnalyticsModalSubmission(preferred);
    } catch {
      /* keep the opened submission */
    } finally {
      setAnalyticsModalHistoryLoading(false);
    }
  };

  useEffect(() => {
    const classId = classData?.id;
    const submissionId = analyticsModalSubmission?.id;
    if (classId == null || submissionId == null) {
      setAnalyticsComments([]);
      return;
    }
    let cancelled = false;
    setAnalyticsCommentsLoading(true);
    setAnalyticsCommentError(null);
    setAnalyticsCommentSent(false);
    setAnalyticsCommentDraft('');
    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/classes/${classId}/submissions/${submissionId}/comments`,
          { credentials: 'include' },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !Array.isArray(data.comments)) {
          setAnalyticsComments([]);
          return;
        }
        setAnalyticsComments(data.comments);
        setLessonAnalytics((prev) => {
          if (!prev || analyticsModalSubmission?.userId == null) return prev;
          return {
            ...prev,
            submissions: prev.submissions.map((s) => {
              if (s.userId !== analyticsModalSubmission.userId || s.problemId !== analyticsModalSubmission.problemId) {
                return s;
              }
              if (!s.feedback) return s;
              return { ...s, feedback: { ...s.feedback, unread_reply: false } };
            }),
          };
        });
      } catch {
        if (!cancelled) setAnalyticsComments([]);
      } finally {
        if (!cancelled) setAnalyticsCommentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classData?.id, analyticsModalSubmission?.id]);

  const sendAnalyticsComment = async () => {
    if (!classData || analyticsModalSubmission?.id == null || analyticsCommentSending) return;
    const body = analyticsCommentDraft.trim();
    if (!body) {
      setAnalyticsCommentError(t('classEdit.feedbackEmpty'));
      return;
    }
    setAnalyticsCommentSending(true);
    setAnalyticsCommentError(null);
    setAnalyticsCommentSent(false);
    try {
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/submissions/${analyticsModalSubmission.id}/comments`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAnalyticsCommentError(data?.message || t('classEdit.feedbackSendError'));
        return;
      }
      if (data?.comment) {
        setAnalyticsComments((prev) => [...prev, data.comment]);
      }
      setAnalyticsCommentDraft('');
      setAnalyticsCommentSent(true);
      setLessonAnalytics((prev) => {
        if (!prev || analyticsModalSubmission?.userId == null) return prev;
        return {
          ...prev,
          submissions: prev.submissions.map((s) => (
            s.userId === analyticsModalSubmission.userId && s.problemId === analyticsModalSubmission.problemId
              ? { ...s, feedback: { status: 'unread', unread_reply: false } }
              : s
          )),
        };
      });
    } catch {
      setAnalyticsCommentError(t('classEdit.feedbackSendError'));
    } finally {
      setAnalyticsCommentSending(false);
    }
  };

  const feedbackBadgeTitle = (fb: NonNullable<AnalyticsSubmission['feedback']>) => {
    if (fb.status === 'replied' && fb.unread_reply) return t('classEdit.feedbackBadgeUnreadReply');
    if (fb.status === 'replied') return t('classEdit.feedbackBadgeReplied');
    if (fb.status === 'read') return t('classEdit.feedbackBadgeRead');
    return t('classEdit.feedbackBadgeUnread');
  };

  const formatAnalyticsDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(uiLang === 'ru' ? 'ru-RU' : undefined, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const exportAnalyticsCsv = () => {
    if (!lessonAnalytics) return;
    const headers = [
      t('classEdit.studentColumn'),
      'username',
      ...lessonAnalytics.problems.map((p) => p.title),
    ];
    const rows = filteredAnalyticsStudents.map((s) => {
      const cells = lessonAnalytics.problems.map((p) => {
        const sub = lessonAnalytics.submissions.find(
          (x) => x.userId === s.userId && x.problemId === p.problemId
        );
        if (!sub || !submissionInPeriod(sub)) return '';
        return `${sub.status};${sub.language};${sub.submitted_at}`;
      });
      return [studentDisplayName(s), s.username || '', ...cells]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const csv = [headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','), ...rows].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class-analytics-${lessonAnalytics.lesson.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleAnalyticsStudent = (userId: number) => {
    setAnalyticsStudentFilter((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const students = useMemo(
    () => (classData?.members || []).filter((m) => m.role === 'student'),
    [classData]
  );
  const teachers = useMemo(
    () => (classData?.members || []).filter((m) => m.role === 'teacher'),
    [classData]
  );
  const totalProblems = classData?.total_problems_in_class
    ?? classData?.lessons.reduce((sum, l) => sum + l.problems.length, 0)
    ?? 0;
  const totalSolved = students.reduce((sum, m) => sum + (m.solved_count ?? 0), 0);
  const avgProgress = students.length && totalProblems
    ? Math.round(
        (students.reduce((sum, m) => sum + Math.min(1, (m.solved_count ?? 0) / totalProblems), 0) /
          students.length) *
          100
      )
    : 0;

  const memberActivityRef = (m: Member) => m.last_activity_at || m.joined_at || null;

  const memberDaysSinceActivity = (m: Member) => {
    const ref = memberActivityRef(m);
    if (!ref) return Number.POSITIVE_INFINITY;
    return (Date.now() - new Date(ref).getTime()) / 86400000;
  };

  const getMemberStatus = (m: Member): 'active' | 'at_risk' | 'inactive' => {
    const days = memberDaysSinceActivity(m);
    if (days <= 7) return 'active';
    if (days > 14) return 'inactive';
    return 'at_risk';
  };

  const memberStatusLabel = (status: 'active' | 'at_risk' | 'inactive') => {
    if (status === 'active') return t('classEdit.studentStatusActive');
    if (status === 'inactive') return t('classEdit.studentStatusInactive');
    return t('classEdit.studentStatusAtRisk');
  };

  const memberShortName = (m: Member) => {
    const first = (m.firstName || '').trim();
    const last = (m.lastName || '').trim();
    if (first && last) return `${first} ${last.charAt(0)}.`;
    if (first) return first;
    return m.username || `ID ${m.user_id}`;
  };

  const memberInitials = (m: Member) => {
    const a = (m.firstName || '').trim();
    const b = (m.lastName || '').trim();
    if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || '?';
    return (m.username || '?').slice(0, 2).toUpperCase();
  };

  const memberProgress = (m: Member) =>
    totalProblems > 0 ? Math.round(Math.min(1, (m.solved_count ?? 0) / totalProblems) * 100) : 0;

  const formatMemberActivity = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startToday - startThat) / 86400000);
    const time = d.toLocaleTimeString(uiLang === 'ru' ? 'ru-RU' : undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    if (diffDays === 0) return `${t('classEdit.activityToday')}, ${time}`;
    if (diffDays === 1) return `${t('classEdit.activityYesterday')}, ${time}`;
    const date = d.toLocaleDateString(uiLang === 'ru' ? 'ru-RU' : undefined, {
      day: 'numeric',
      month: 'short',
    });
    return `${date}, ${time}`;
  };

  const todayYmd = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  };

  const parseDueDate = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

  const formatDueDate = (ymd: string) =>
    parseDueDate(ymd).toLocaleDateString(uiLang === 'ru' ? 'ru-RU' : undefined, {
      day: 'numeric',
      month: 'short',
    });

  const lessonIsPublished = (lesson: Lesson) => lesson.is_published !== false;

  const lessonIsOverdue = (lesson: Lesson) => Boolean(lesson.due_at && lesson.due_at < todayYmd());

  const studentsTabStats = useMemo(() => {
    const active = students.filter((m) => memberDaysSinceActivity(m) <= 7).length;
    const inactive = students.filter((m) => memberDaysSinceActivity(m) > 14).length;
    const newWeek = students.filter((m) => {
      if (!m.joined_at) return false;
      return (Date.now() - new Date(m.joined_at).getTime()) / 86400000 <= 7;
    }).length;
    return { active, inactive, newWeek, total: students.length };
  }, [students]);

  const filteredStudentsList = useMemo(() => {
    let list = [...students];
    const q = studentsSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((m) => {
        const name = `${m.firstName || ''} ${m.lastName || ''} ${m.username || ''} ${m.email || ''}`.toLowerCase();
        return name.includes(q);
      });
    }
    if (studentsStatusFilter === 'missing') {
      const missing = new Set(assignmentProgress?.missingUserIds || []);
      list = list.filter((m) => missing.has(Number(m.user_id)));
    } else if (studentsStatusFilter !== 'all') {
      list = list.filter((m) => getMemberStatus(m) === studentsStatusFilter);
    }
    list.sort((a, b) => {
      if (studentsSort === 'name') {
        return memberShortName(a).localeCompare(memberShortName(b), uiLang === 'ru' ? 'ru' : undefined);
      }
      if (studentsSort === 'progress') {
        return memberProgress(b) - memberProgress(a);
      }
      const ta = memberActivityRef(a) ? new Date(memberActivityRef(a)!).getTime() : 0;
      const tb = memberActivityRef(b) ? new Date(memberActivityRef(b)!).getTime() : 0;
      return tb - ta;
    });
    return list;
  }, [students, studentsSearch, studentsStatusFilter, studentsSort, totalProblems, uiLang, t, assignmentProgress]);

  const studentsTotalPages = Math.max(1, Math.ceil(filteredStudentsList.length / studentsPageSize));
  const pagedStudents = useMemo(() => {
    const page = Math.min(studentsPage, studentsTotalPages);
    const start = (page - 1) * studentsPageSize;
    return filteredStudentsList.slice(start, start + studentsPageSize);
  }, [filteredStudentsList, studentsPage, studentsTotalPages]);

  useEffect(() => {
    setStudentsPage(1);
  }, [studentsSearch, studentsStatusFilter, studentsSort]);

  useEffect(() => {
    if (studentsPage > studentsTotalPages) setStudentsPage(studentsTotalPages);
  }, [studentsPage, studentsTotalPages]);

  const exportStudents = (format: 'json' | 'csv' | 'xlsx') => {
    const colStudent = t('classEdit.studentsColStudent');
    const colNickname = t('classEdit.studentsColNickname');
    const colEmail = 'email';
    const colProgress = t('classEdit.studentsColProgress');
    const colSolved = t('classEdit.studentsColSolved');
    const colActivity = t('classEdit.studentsColActivity');
    const colStatus = t('classEdit.studentsColStatus');

    const rows = filteredStudentsList.map((m) => {
      const status = getMemberStatus(m);
      const statusLabel =
        status === 'active'
          ? t('classEdit.studentStatusActive')
          : status === 'inactive'
            ? t('classEdit.studentStatusInactive')
            : t('classEdit.studentStatusAtRisk');
      return {
        [colStudent]: memberShortName(m),
        [colNickname]: m.username || '',
        [colEmail]: m.email || '',
        [colProgress]: `${memberProgress(m)}%`,
        [colSolved]: `${m.solved_count ?? 0} / ${totalProblems}`,
        [colActivity]: memberActivityRef(m) || '',
        [colStatus]: statusLabel,
      };
    });

    const stamp = new Date().toISOString().slice(0, 10);
    const base = `class-students-${classData?.id || 'export'}-${stamp}`;

    const downloadBlob = (blob: Blob, filename: string) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    };

    if (format === 'json') {
      const payload = {
        exportedAt: new Date().toISOString(),
        classId: classData?.id ?? null,
        className: classData?.name ?? null,
        count: rows.length,
        students: rows,
      };
      downloadBlob(
        new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' }),
        `${base}.json`,
      );
      return;
    }

    if (format === 'csv') {
      const headers = [
        colStudent,
        colNickname,
        colEmail,
        colProgress,
        colSolved,
        colActivity,
        colStatus,
      ];
      const escape = (v: unknown) => {
        const s = v == null ? '' : String(v);
        if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const lines = [
        headers.map(escape).join(','),
        ...rows.map((row) => headers.map((h) => escape((row as Record<string, unknown>)[h])).join(',')),
      ];
      downloadBlob(
        new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' }),
        `${base}.csv`,
      );
      return;
    }

    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Students');
    XLSX.writeFile(book, `${base}.xlsx`);
  };

  const filteredLessons = useMemo(() => {
    if (!classData) return [];
    const q = programSearch.trim().toLowerCase();
    if (!q) return classData.lessons;
    return classData.lessons.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        l.problems.some((p) => (p.problem?.title || '').toLowerCase().includes(q))
    );
  }, [classData, programSearch]);

  const fetchCustomProblems = useCallback(async (search: string) => {
    if (!classData?.id) return;
    setCustomProblemsLoading(true);
    try {
      const params = new URLSearchParams({
        lang: uiLang,
        limit: '50',
        page: '1',
      });
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(
        `${API_BASE}/api/classes/${classData.id}/custom-problems?${params}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        setCustomProblemsList([]);
        setCustomProblemsTotal(0);
        return;
      }
      const data = await res.json();
      setCustomProblemsList(
        (data.problems || []).map((p: {
          id: number;
          title: string;
          difficulty: string;
          topic?: string;
          slug?: string | null;
          lessons?: { id: number; title: string }[];
        }) => ({
          id: p.id,
          title: p.title,
          difficulty: p.difficulty,
          topic: p.topic,
          slug: p.slug,
          lessons: Array.isArray(p.lessons) ? p.lessons : [],
        })),
      );
      setCustomProblemsTotal(Number(data.total) || 0);
    } catch {
      setCustomProblemsList([]);
      setCustomProblemsTotal(0);
    } finally {
      setCustomProblemsLoading(false);
    }
  }, [classData?.id, uiLang]);

  useEffect(() => {
    if (activeTab !== 'customProblems' || !canEdit || !classData?.id) return;
    const timer = setTimeout(() => {
      fetchCustomProblems(customProblemsSearch);
    }, 250);
    return () => clearTimeout(timer);
  }, [activeTab, canEdit, classData?.id, customProblemsSearch, fetchCustomProblems]);

  useEffect(() => {
    const next = searchParams.get('tab');
    if (
      next === 'assignment' ||
      next === 'customProblems' ||
      next === 'students' ||
      next === 'analytics' ||
      next === 'settings' ||
      next === 'overview'
    ) {
      setActiveTab(next);
    }
    const student = Number(searchParams.get('student'));
    if (Number.isInteger(student) && student > 0) {
      setAnalyticsStudentFilter([student]);
    }
    if (searchParams.get('status') === 'missing') {
      setStudentsStatusFilter('missing');
    }
  }, [searchParams]);

  const selectTab = (tab: ClassTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    next.delete('student');
    if (tab !== 'students') next.delete('status');
    setAnalyticsStudentFilter([]);
    setSearchParams(next, { replace: true });
  };

  const openMissingStudents = () => {
    setStudentsStatusFilter('missing');
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'students');
    next.set('status', 'missing');
    next.delete('student');
    setSearchParams(next, { replace: true });
    setActiveTab('students');
  };

  const openMissingAnalytics = (lessonId: number, userIds: number[]) => {
    setSelectedLessonIdForAnalytics(lessonId);
    setAnalyticsStudentFilter(userIds);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'analytics');
    next.delete('student');
    next.delete('status');
    setSearchParams(next, { replace: true });
    setActiveTab('analytics');
  };

  const openStudentWorkbook = (userId: number, lessonId?: number) => {
    if (!canEdit) return;
    setAnalyticsStudentFilter([userId]);
    setAnalyticsStudentSearch('');
    if (lessonId) setSelectedLessonIdForAnalytics(lessonId);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'analytics');
    next.set('student', String(userId));
    setSearchParams(next, { replace: true });
    setActiveTab('analytics');
  };

  const lessonIdForProblem = (problemId: number) =>
    classData?.lessons.find((l) => l.problems.some((p) => p.problem_id === problemId))?.id;

  const activityPersonName = (item: ClassActivityItem) => {
    const first = (item.firstName || '').trim();
    const last = (item.lastName || '').trim();
    if (first && last) return `${first} ${last.charAt(0)}.`;
    if (first) return first;
    return item.username || `ID ${item.userId}`;
  };

  const activityPersonInitials = (item: ClassActivityItem) => {
    const a = (item.firstName || '').trim();
    const b = (item.lastName || '').trim();
    if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || '?';
    return (item.username || '?').slice(0, 2).toUpperCase();
  };

  const activityVerb = (status: string) => {
    if (status === 'Accepted') return t('classEdit.activitySolved');
    if (status === 'Pending') return t('classEdit.activityReview');
    return t('classEdit.activityFailed');
  };

  const renderActivityWidget = () => (
    <div className="class-dash__widget">
      <h3 className="class-dash__widget-title">{t('classEdit.activityTitle')}</h3>
      {classActivityLoading ? (
        <p className="class-dash__empty">{t('classEdit.loading')}</p>
      ) : classActivity.length === 0 ? (
        <p className="class-dash__empty">{t('classEdit.activityEmpty')}</p>
      ) : (
        <ul className="class-dash__activity">
          {classActivity.map((item, idx) => (
            <li key={`${item.userId}-${item.problemId}-${item.at}-${idx}`}>
              <button
                type="button"
                className="class-dash__activity-item class-dash__activity-item--btn"
                onClick={() => openStudentWorkbook(item.userId, lessonIdForProblem(item.problemId))}
              >
                <span className="class-dash__activity-avatar">
                  {item.avatar ? (
                    <img src={item.avatar} alt="" />
                  ) : (
                    activityPersonInitials(item)
                  )}
                </span>
                <div className="class-dash__activity-text">
                  <p>
                    <strong>{activityPersonName(item)}</strong>{' '}
                    {activityVerb(item.status)}{' '}
                    <span className="class-dash__activity-problem">{item.problemTitle}</span>
                  </p>
                  <span className="class-dash__activity-time">
                    {formatMemberActivity(item.at)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderDeadlinesWidget = () => {
    const items = (classData?.lessons || [])
      .filter((lesson) => lessonIsPublished(lesson) && lesson.due_at)
      .sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)))
      .slice(0, 6);
    return (
      <div className="class-dash__widget">
        <h3 className="class-dash__widget-title">{t('classEdit.deadlinesTitle')}</h3>
        {items.length === 0 ? (
          <>
            <p className="class-dash__empty">{t('classEdit.deadlinesEmpty')}</p>
            <p className="class-dash__empty">{t('classEdit.deadlineHint')}</p>
          </>
        ) : (
          <ul className="class-dash__deadlines">
            {items.map((lesson) => {
              const due = lesson.due_at as string;
              const overdue = lessonIsOverdue(lesson);
              const date = parseDueDate(due);
              return (
                <li key={lesson.id}>
                  <button
                    type="button"
                    className={`class-dash__deadline${overdue ? ' is-overdue' : ''}`}
                    onClick={() => {
                      setExpandedLessons((prev) => new Set([...prev, lesson.id]));
                    }}
                  >
                    <span className="class-dash__deadline-date">
                      <span>{date.getDate()}</span>
                      <span>
                        {date.toLocaleDateString(uiLang === 'ru' ? 'ru-RU' : undefined, { month: 'short' })}
                      </span>
                    </span>
                    <span className="class-dash__deadline-body">
                      <strong>{lesson.title}</strong>
                      <span>
                        {overdue
                          ? t('classEdit.deadlineOverdue')
                          : t('classEdit.deadlineDue').replace('{date}', formatDueDate(due))}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const startCreateCustomProblem = () => {
    if (!classData || !canEdit) return;
    navigate(`/problems/create?classId=${classData.id}`);
  };

  if (authLoading) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-loading">{t('classEdit.loading')}</div>
      </div>
    );
  }

  if (!canAccessClassPage) return null;

  if (loading) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-loading">{t('classEdit.loading')}</div>
      </div>
    );
  }

  if (classIdFromRoute && !classData) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-card class-edit-create">
          <h1>{t('classEdit.classTitle')}</h1>
          <div className="class-edit-error" role="alert">{error || t('classEdit.classNotFoundOrNoAccess')}</div>
          <Link to="/profile" className="class-edit-btn class-edit-btn-primary" style={{ marginTop: 16, display: 'inline-block' }}>
            {t('classEdit.toProfile')}
          </Link>
        </div>
      </div>
    );
  }

  if (createMode) {
    const premiumRequired = !hasActiveSubscription;
    return (
      <div className="class-edit-page">
        <div className="class-edit-card class-edit-create">
          <h1>{t('classEdit.createClass')}</h1>
          <p className="class-edit-create-desc">{t('classEdit.createClassDesc')}</p>
          {premiumRequired && (
            <div className="class-edit-premium-block">
              <span className="class-edit-premium-icon"><FaLock /></span>
              <p>{t('classEdit.premiumRequiredCreate')}</p>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="class-edit-btn class-edit-btn-primary"
              >
                {t('classEdit.goToSubscription')}
              </button>
            </div>
          )}
          {!premiumRequired && (
            <form onSubmit={handleCreateClass}>
              <input
                type="text"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                placeholder={t('classEdit.classNamePlaceholder')}
                maxLength={200}
                className="class-edit-input"
              />
              <button type="submit" disabled={createClassLoading || !newClassName.trim()} className="class-edit-btn class-edit-btn-primary">
                {createClassLoading ? t('classEdit.creating') : t('classEdit.createClassButton')}
              </button>
            </form>
          )}
          {error && <div className="class-edit-error">{error}</div>}
        </div>
      </div>
    );
  }

  if (!classData) return null;

  const navItems: { id: ClassTab; label: string; icon: React.ReactNode; teacherOnly?: boolean }[] = [
    { id: 'overview', label: t('classEdit.overviewTab'), icon: <FaThLarge /> },
    { id: 'assignment', label: t('classEdit.assignmentTab'), icon: <FaCalendarAlt />, teacherOnly: true },
    { id: 'students', label: t('classEdit.studentsTab'), icon: <FaUsers /> },
    { id: 'analytics', label: t('classEdit.analyticsTab'), icon: <FaChartBar />, teacherOnly: true },
    { id: 'customProblems', label: t('classEdit.customProblemsTab'), icon: <FaPencilAlt />, teacherOnly: true },
    { id: 'settings', label: t('classEdit.settingsTab'), icon: <FaCog />, teacherOnly: true },
  ];

  const renderAddProblem = (lesson: Lesson) => {
    if (!canEdit) return null;
    return (
      <div className="class-dash__task-actions">
        <button
          type="button"
          onClick={() => openAddProblemModal(lesson.id)}
          className="class-edit-btn class-edit-btn-outline class-edit-btn-sm class-dash__add-task"
        >
          <FaPlus /> {t('classEdit.addProblem')}
        </button>
      </div>
    );
  };

  const renderLessonsList = (editable: boolean) => (
    <ul className="class-dash__lessons">
      {filteredLessons.length === 0 && (
        <li className="class-dash__empty">{t('classEdit.programEmpty')}</li>
      )}
      {filteredLessons.map((lesson, idx) => {
        const isOpen = expandedLessons.has(lesson.id);
        const isLessonDragging = dragLessonId === lesson.id;
        const isLessonDragOver =
          dragOverLessonId === lesson.id && dragLessonId != null && dragLessonId !== lesson.id;
        return (
          <li
            key={lesson.id}
            className={`class-dash__lesson${isOpen ? ' is-open' : ''}${isLessonDragging ? ' is-dragging' : ''}${isLessonDragOver ? ' is-drag-over' : ''}`}
            onDragOver={
              editable
                ? (e) => {
                    if (dragLessonId == null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverLessonId !== lesson.id) setDragOverLessonId(lesson.id);
                  }
                : undefined
            }
            onDrop={
              editable
                ? (e) => {
                    if (dragLessonId == null) return;
                    e.preventDefault();
                    const fromId = dragLessonId || Number(e.dataTransfer.getData('text/plain'));
                    setDragLessonId(null);
                    setDragOverLessonId(null);
                    if (!fromId || fromId === lesson.id || !classData) return;
                    const ids = classData.lessons.map((l) => l.id);
                    const fromIdx = ids.indexOf(fromId);
                    const toIdx = ids.indexOf(lesson.id);
                    if (fromIdx < 0 || toIdx < 0) return;
                    const next = [...ids];
                    next.splice(fromIdx, 1);
                    next.splice(toIdx, 0, fromId);
                    handleReorderLessons(next);
                  }
                : undefined
            }
            onDragLeave={
              editable
                ? () => {
                    if (dragOverLessonId === lesson.id) setDragOverLessonId(null);
                  }
                : undefined
            }
          >
            <div className="class-dash__lesson-head">
              {editable && (
                <button
                  type="button"
                  className="class-dash__task-handle"
                  draggable
                  title={t('classEdit.dragToReorder')}
                  aria-label={t('classEdit.dragToReorder')}
                  onClick={(e) => e.stopPropagation()}
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(lesson.id));
                    setDragLessonId(lesson.id);
                    setDragProblem(null);
                    setDragOverProblemId(null);
                  }}
                  onDragEnd={() => {
                    setDragLessonId(null);
                    setDragOverLessonId(null);
                  }}
                >
                  <FaGripVertical aria-hidden />
                </button>
              )}
              <button
                type="button"
                className="class-dash__lesson-toggle"
                onClick={() => toggleLesson(lesson.id)}
                aria-expanded={isOpen}
              >
                <FaChevronRight className="class-dash__lesson-chevron" aria-hidden />
                <span className="class-dash__lesson-index">{idx + 1}</span>
                {editable && editingLessonId === lesson.id ? (
                  <input
                    type="text"
                    value={editingLessonTitle}
                    onChange={(e) => setEditingLessonTitle(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="class-edit-input class-edit-input-sm"
                    style={{ flex: 1 }}
                    autoFocus
                  />
                ) : (
                  <span className="class-dash__lesson-title">{lesson.title}</span>
                )}
                {!lessonIsPublished(lesson) ? (
                  <span className="class-dash__lesson-badge class-dash__lesson-badge--draft">
                    <FaLock aria-hidden />
                    {t('classEdit.lessonDraft')}
                  </span>
                ) : lesson.due_at ? (
                  <span className={`class-dash__lesson-badge${lessonIsOverdue(lesson) ? ' is-overdue' : ''}`}>
                    <FaCalendarAlt aria-hidden />
                    {lessonIsOverdue(lesson)
                      ? t('classEdit.deadlineOverdue')
                      : formatDueDate(lesson.due_at)}
                  </span>
                ) : null}
                <span className="class-dash__lesson-count">
                  {lesson.problems.length}{' '}
                  {t('classEdit.tasksCount')}
                </span>
              </button>
              {editable && (
                <div className="class-dash__lesson-actions">
                  {editingLessonId === lesson.id ? (
                    <>
                      <button type="button" onClick={() => handleUpdateLesson(lesson.id)} className="class-edit-btn class-edit-btn-icon class-edit-btn-sm" title={t('classEdit.save')}>
                        <FaCheck />
                      </button>
                      <button type="button" onClick={() => { setEditingLessonId(null); setEditingLessonTitle(''); }} className="class-edit-btn class-edit-btn-icon class-edit-btn-sm" title={t('classEdit.cancel')}>
                        <FaTimes />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { setEditingLessonId(lesson.id); setEditingLessonTitle(lesson.title); }}
                        className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                        title={t('classEdit.renameLesson')}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteLessonModal({ id: lesson.id, title: lesson.title })}
                        className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                        title={t('classEdit.deleteLesson')}
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {isOpen && (
              <div className="class-dash__lesson-body">
                {editable && (
                  <div className="class-dash__assign">
                    <button
                      type="button"
                      className={`class-edit-btn class-edit-btn-sm${lessonIsPublished(lesson) ? ' class-edit-btn-outline' : ' class-edit-btn-primary'}`}
                      onClick={() =>
                        handlePatchLesson(lesson.id, { is_published: !lessonIsPublished(lesson) })
                      }
                    >
                      {lessonIsPublished(lesson)
                        ? t('classEdit.unpublishLesson')
                        : t('classEdit.publishLesson')}
                    </button>
                    <label className="class-dash__assign-due">
                      <FaCalendarAlt aria-hidden />
                      <span>{t('classEdit.lessonDue')}</span>
                      <input
                        type="date"
                        value={lesson.due_at || ''}
                        onChange={(e) =>
                          handlePatchLesson(lesson.id, { due_at: e.target.value || null })
                        }
                      />
                    </label>
                  </div>
                )}
                {lesson.problems.map((pr) => {
                  const isDragging =
                    dragProblem?.lessonId === lesson.id &&
                    dragProblem?.problemId === pr.problem_id;
                  const isDragOver =
                    dragOverProblemId === pr.problem_id &&
                    dragProblem?.lessonId === lesson.id &&
                    dragProblem?.problemId !== pr.problem_id;
                  return (
                    <div
                      key={pr.id}
                      className={`class-dash__task-row${isDragging ? ' is-dragging' : ''}${isDragOver ? ' is-drag-over' : ''}`}
                      onDragOver={
                        editable
                          ? (e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              if (dragOverProblemId !== pr.problem_id) {
                                setDragOverProblemId(pr.problem_id);
                              }
                            }
                          : undefined
                      }
                      onDrop={
                        editable
                          ? (e) => {
                              e.preventDefault();
                              const fromId = dragProblem?.lessonId === lesson.id
                                ? dragProblem.problemId
                                : Number(e.dataTransfer.getData('text/plain'));
                              setDragProblem(null);
                              setDragOverProblemId(null);
                              if (!fromId || fromId === pr.problem_id) return;
                              const ids = lesson.problems.map((p) => p.problem_id);
                              const fromIdx = ids.indexOf(fromId);
                              const toIdx = ids.indexOf(pr.problem_id);
                              if (fromIdx < 0 || toIdx < 0) return;
                              const next = [...ids];
                              next.splice(fromIdx, 1);
                              next.splice(toIdx, 0, fromId);
                              handleReorderProblems(lesson.id, next);
                            }
                          : undefined
                      }
                      onDragLeave={
                        editable
                          ? () => {
                              if (dragOverProblemId === pr.problem_id) {
                                setDragOverProblemId(null);
                              }
                            }
                          : undefined
                      }
                    >
                      {editable && (
                        <button
                          type="button"
                          className="class-dash__task-handle"
                          draggable
                          title={t('classEdit.dragToReorder')}
                          aria-label={t('classEdit.dragToReorder')}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(pr.problem_id));
                            setDragProblem({ lessonId: lesson.id, problemId: pr.problem_id });
                          }}
                          onDragEnd={() => {
                            setDragProblem(null);
                            setDragOverProblemId(null);
                          }}
                        >
                          <FaGripVertical aria-hidden />
                        </button>
                      )}
                      <Link
                        to={pr.problem ? problemPath(pr.problem) : `/problems/${pr.problem_id}`}
                        className="class-dash__task"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {!editable && <FaCode className="class-dash__task-icon" aria-hidden />}
                        <span className="class-dash__task-title">
                          {pr.problem?.title ?? `${t('classEdit.problemIdPrefix')}${pr.problem_id}`}
                          {pr.problem?.unlisted ? (
                            <span className="class-dash__task-custom-badge">{t('classEdit.customProblemBadge')}</span>
                          ) : null}
                        </span>
                        {pr.problem?.difficulty && (
                          <span
                            className="class-dash__task-diff"
                            style={{ color: getDifficultyColor(pr.problem.difficulty) }}
                          >
                            {getDifficultyLabel(pr.problem.difficulty)}
                          </span>
                        )}
                      </Link>
                      {editable && (
                        <button
                          type="button"
                          onClick={() =>
                            setRemoveProblemModal({
                              lessonId: lesson.id,
                              problemId: pr.problem_id,
                              title: pr.problem?.title ?? `${t('classEdit.problemIdPrefix')}${pr.problem_id}`,
                            })
                          }
                          className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                          title={t('classEdit.removeFromLesson')}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  );
                })}
                {renderAddProblem(lesson)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="class-edit-page">
      <div className="class-dash">
        <aside className="class-dash__rail">
          <div className="class-dash__class-select-wrap" ref={classSelectRef}>
            <button
              type="button"
              className={`class-dash__class-select${classSelectOpen ? ' is-open' : ''}`}
              onClick={() => setClassSelectOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={classSelectOpen}
            >
              <span className="class-dash__class-select-icon" aria-hidden>
                <FaGraduationCap />
              </span>
              <div className="class-dash__class-select-text">
                <span className="class-dash__class-select-label">{t('classEdit.myClass')}</span>
                <span className="class-dash__class-select-name">{classData.name}</span>
              </div>
              <FaChevronDown className="class-dash__class-select-chevron" aria-hidden />
            </button>
            {classSelectOpen && (
              <div className="class-dash__class-menu" role="listbox">
                <button
                  type="button"
                  className="class-dash__class-menu-item"
                  onClick={() => {
                    setClassSelectOpen(false);
                    navigate('/my-class');
                  }}
                >
                  {t('navigation.myClass')}
                </button>
                {(classData.teacher_classes && classData.teacher_classes.length > 0
                  ? classData.teacher_classes
                  : [{ id: classData.id, name: classData.name }]
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={item.id === classData.id}
                    className={`class-dash__class-menu-item${item.id === classData.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setClassSelectOpen(false);
                      if (item.id === classData.id) return;
                      navigate(`/class/${item.id}`);
                    }}
                  >
                    {item.name}
                  </button>
                ))}
                {canEdit && (classData.teacher_classes || []).length < 3 && (
                  <button
                    type="button"
                    className="class-dash__class-menu-item class-dash__class-menu-item--create"
                    onClick={() => {
                      setClassSelectOpen(false);
                      setCreateClassModalName('');
                      setError(null);
                      setCreateClassModalOpen(true);
                    }}
                  >
                    <FaPlus aria-hidden /> {t('classEdit.classSelectCreate')}
                  </button>
                )}
              </div>
            )}
          </div>

          <nav className="class-dash__nav" aria-label={t('classEdit.classNavAria')}>
            {navItems
              .filter((item) => !item.teacherOnly || canEdit)
              .map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`class-dash__nav-item${activeTab === item.id ? ' is-active' : ''}`}
                  onClick={() => selectTab(item.id)}
                >
                  <span className="class-dash__nav-icon" aria-hidden>{item.icon}</span>
                  {item.label}
                </button>
              ))}
          </nav>

          <div className="class-dash__rail-footer">
            <div className="class-dash__premium-card">
              <div className="class-dash__premium-card-head">
                <FaGem aria-hidden /> Premium
              </div>
              <p>{t('classEdit.premiumCardText')}</p>
              <Link to="/subscription" className="class-edit-btn class-edit-btn-sm">
                {t('classEdit.manageSubscription')}
              </Link>
            </div>
            <Link to="/faq" className="class-dash__help-link">
              <FaQuestionCircle aria-hidden /> {t('classEdit.helpCenter')}
            </Link>
          </div>
        </aside>

        <div className="class-dash__body">
          {isTeacher && !canEdit && (
            <div className="class-dash__banner">
              <span className="class-edit-premium-icon"><FaLock /></span>
              <p>{t('classEdit.premiumBannerText')}</p>
              <Link to="/subscription" className="class-edit-btn class-edit-btn-primary">
                {t('classEdit.extendSubscription')}
              </Link>
            </div>
          )}

          <header className="class-dash__hero">
            <div className="class-dash__hero-main">
              <div className="class-dash__hero-title-row">
                <h1 className="class-dash__hero-title">{classData.name}</h1>
                <span className="class-dash__hero-tag">{classData.invite_code}</span>
              </div>
              <p className="class-dash__hero-meta">
                <span>
                  <strong>{students.length}</strong> {t('classEdit.studentsLabel')}
                </span>
                <span>
                  {t('classEdit.teacherLabel')}:{' '}
                  <strong>{teachers.map((m) => m.username || `ID ${m.user_id}`).join(', ') || '—'}</strong>
                </span>
              </p>
            </div>
            <div className="class-dash__hero-actions">
              <button
                type="button"
                className="class-edit-btn class-edit-btn-outline"
                onClick={() => selectTab('students')}
              >
                <FaUserPlus /> {t('classEdit.inviteStudentsCta')}
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-primary"
                  onClick={() => selectTab('overview')}
                >
                  <FaPlus /> {t('classEdit.createLessonCta')}
                </button>
              )}
            </div>
          </header>

          {(activeTab === 'overview') && (
            <div className="class-dash__stats">
              <div className="class-dash__stat">
                <span className="class-dash__stat-icon class-dash__stat-icon--students" aria-hidden>
                  <FaUsers />
                </span>
                <div className="class-dash__stat-body">
                  <span className="class-dash__stat-label">{t('classEdit.statStudents')}</span>
                  <span className="class-dash__stat-value">{students.length}</span>
                </div>
              </div>
              <div className="class-dash__stat">
                <span className="class-dash__stat-icon class-dash__stat-icon--lessons" aria-hidden>
                  <FaBookOpen />
                </span>
                <div className="class-dash__stat-body">
                  <span className="class-dash__stat-label">{t('classEdit.statLessons')}</span>
                  <span className="class-dash__stat-value">{classData.lessons.length}</span>
                  <span className="class-dash__stat-hint">
                    {t('classEdit.statLessonsHint').replace(
                      '{published}',
                      String(classData.lessons.filter(lessonIsPublished).length),
                    )}
                  </span>
                </div>
              </div>
              <div className="class-dash__stat">
                <span className="class-dash__stat-icon class-dash__stat-icon--solved" aria-hidden>
                  <FaCheck />
                </span>
                <div className="class-dash__stat-body">
                  <span className="class-dash__stat-label">{t('classEdit.statSolved')}</span>
                  <span className="class-dash__stat-value">{totalSolved}</span>
                  <span className="class-dash__stat-hint">
                    {totalProblems} {t('classEdit.statProblemsTotal')}
                  </span>
                </div>
              </div>
              <div className="class-dash__stat">
                <span className="class-dash__stat-icon class-dash__stat-icon--progress" aria-hidden>
                  <FaChartBar />
                </span>
                <div className="class-dash__stat-body">
                  <span className="class-dash__stat-label">{t('classEdit.statProgress')}</span>
                  <span className="class-dash__stat-value">{avgProgress}%</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="class-dash__grid">
              <section className="class-dash__panel">
                <div className="class-dash__panel-head">
                  <h2 className="class-dash__panel-title">{t('classEdit.courseProgram')}</h2>
                  <div className="class-dash__panel-tools">
                    <label className="class-dash__search">
                      <FaSearch aria-hidden />
                      <input
                        type="search"
                        value={programSearch}
                        onChange={(e) => setProgramSearch(e.target.value)}
                        placeholder={t('classEdit.searchProgram')}
                        aria-label={t('classEdit.searchProgram')}
                      />
                    </label>
                  </div>
                </div>
                {canEdit && (
                  <form onSubmit={handleAddLesson} className="class-dash__add-lesson">
                    <input
                      type="text"
                      value={newLessonTitle}
                      onChange={(e) => setNewLessonTitle(e.target.value)}
                      placeholder={t('classEdit.newLessonPlaceholder')}
                      maxLength={300}
                      className="class-edit-input"
                    />
                    <button type="submit" disabled={addingLesson || !newLessonTitle.trim()} className="class-edit-btn class-edit-btn-primary">
                      <FaPlus /> {t('classEdit.addLesson')}
                    </button>
                  </form>
                )}
                {canEdit && (
                  <p className="class-edit-invite-hint class-dash__add-lesson-hint">
                    {t('classEdit.addLessonHint')}
                  </p>
                )}
                {renderLessonsList(canEdit)}
              </section>

              <aside className="class-dash__widgets">
                {renderDeadlinesWidget()}
                {renderActivityWidget()}
              </aside>
            </div>
          )}

          {activeTab === 'assignment' && canEdit && (() => {
            const today = localTodayYmd();
            const published = (classData.lessons || []).filter((l) => l.is_published && (l.problems?.length || 0) > 0);
            const currentLesson =
              (classData.lessons || []).find((l) => l.id === assignmentProgress?.lessonId)
              || pickCurrentAssignment(classData.lessons, today);
            const missingMembers = students.filter((m) =>
              (assignmentProgress?.missingUserIds || []).includes(Number(m.user_id))
            );
            const due = currentLesson?.due_at;
            const overdue = Boolean(due && due < today);
            return (
              <div className="class-dash__assignment">
                <section className="class-dash__panel">
                  <div className="class-dash__panel-head">
                    <div>
                      <h2 className="class-dash__panel-title">{t('classEdit.assignmentTitle')}</h2>
                      <p className="class-edit-invite-hint">{t('classEdit.assignmentHint')}</p>
                    </div>
                  </div>

                  {published.length === 0 ? (
                    <div className="class-dash__empty">
                      <p>{t('classEdit.assignmentEmpty')}</p>
                      <button type="button" className="class-edit-btn class-edit-btn-primary" onClick={() => selectTab('overview')}>
                        {t('classEdit.overviewTab')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="class-assignment__picker">
                        <label>
                          {t('classEdit.assignmentCurrent')}
                          <select
                            className="class-edit-input class-assignment__select"
                            value={assignmentProgress?.lessonId || currentLesson?.id || ''}
                            onChange={(e) => {
                              const id = Number(e.target.value);
                              if (id) void fetchAssignmentProgress(classData.id, id);
                            }}
                          >
                            {published.map((l) => (
                              <option key={l.id} value={l.id}>{l.title}</option>
                            ))}
                          </select>
                        </label>
                        {due ? (
                          <span className={`class-assignment__due${overdue ? ' is-overdue' : ''}`}>
                            {overdue ? t('classEdit.assignmentOverdue') : t('classEdit.assignmentDue')}: {due}
                          </span>
                        ) : (
                          <span className="class-assignment__due">{t('classEdit.assignmentNoDue')}</span>
                        )}
                      </div>

                      {assignmentProgress && currentLesson && (
                        <>
                          <div className="class-assignment__stats">
                            <button
                              type="button"
                              className="class-assignment__stat is-missing"
                              onClick={openMissingStudents}
                            >
                              <strong>{assignmentProgress.missingUserIds.length}</strong>
                              <span>{t('classEdit.assignmentMissing')}</span>
                            </button>
                            <div className="class-assignment__stat">
                              <strong>{assignmentProgress.notStartedUserIds.length}</strong>
                              <span>{t('classEdit.assignmentNotStarted')}</span>
                            </div>
                            <div className="class-assignment__stat">
                              <strong>{assignmentProgress.solvedUserIds.length}</strong>
                              <span>{t('classEdit.assignmentSolved')}</span>
                            </div>
                          </div>

                          <div className="class-assignment__actions">
                            <button
                              type="button"
                              className="class-edit-btn class-edit-btn-primary"
                              onClick={openMissingStudents}
                            >
                              {t('classEdit.assignmentOpenMissing')}
                            </button>
                            <button
                              type="button"
                              className="class-edit-btn"
                              onClick={() => openMissingAnalytics(currentLesson.id, assignmentProgress.missingUserIds)}
                            >
                              {t('classEdit.assignmentOpenAnalytics')}
                            </button>
                          </div>

                          <h3 className="class-assignment__list-title">{t('classEdit.assignmentMissingList')}</h3>
                          {missingMembers.length === 0 ? (
                            <p className="class-edit-invite-hint">{t('classEdit.assignmentAllDone')}</p>
                          ) : (
                            <ul className="class-assignment__list">
                              {missingMembers.map((m) => (
                                <li key={m.user_id}>
                                  <button
                                    type="button"
                                    className="class-assignment__student"
                                    onClick={() => openStudentWorkbook(m.user_id, currentLesson.id)}
                                  >
                                    <span className="class-analytics-student__avatar">
                                      {m.avatar ? <img src={m.avatar} alt="" /> : memberInitials(m)}
                                    </span>
                                    <span>
                                      <strong>{memberShortName(m)}</strong>
                                      {m.username ? <span className="class-assignment__handle">@{m.username}</span> : null}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </>
                  )}
                </section>
              </div>
            );
          })()}

          {activeTab === 'customProblems' && canEdit && (
            <section className="class-custom-problems">
              <div className="class-custom-problems__header">
                <div>
                  <h2 className="class-dash__panel-title">{t('classEdit.customProblemsTitle')}</h2>
                  <p className="class-edit-invite-hint">{t('classEdit.customProblemsHint')}</p>
                </div>
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-primary"
                  onClick={startCreateCustomProblem}
                >
                  <FaPlus /> {t('classEdit.customProblemsCreate')}
                </button>
              </div>

              <div className="class-custom-problems__toolbar">
                <label className="class-dash__search class-students__search">
                  <FaSearch aria-hidden />
                  <input
                    type="search"
                    value={customProblemsSearch}
                    onChange={(e) => setCustomProblemsSearch(e.target.value)}
                    placeholder={t('classEdit.customProblemsSearch')}
                    aria-label={t('classEdit.customProblemsSearch')}
                  />
                </label>
                <span className="class-custom-problems__count">
                  {t('classEdit.customProblemsCount').replace(
                    '{count}',
                    String(customProblemsSearch.trim() ? customProblemsList.length : customProblemsTotal || customProblemsList.length),
                  )}
                </span>
              </div>

              {customProblemsLoading ? (
                <div className="class-edit-loading-sm">{t('classEdit.loading')}</div>
              ) : customProblemsList.length === 0 ? (
                <div className="class-custom-problems__empty">
                  <p>
                    {customProblemsSearch.trim()
                      ? t('classEdit.customProblemsEmptySearch')
                      : t('classEdit.customProblemsEmpty')}
                  </p>
                  {!customProblemsSearch.trim() && (
                    <button
                      type="button"
                      className="class-edit-btn class-edit-btn-outline"
                      onClick={startCreateCustomProblem}
                    >
                      <FaPlus /> {t('classEdit.customProblemsCreate')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="class-custom-problems__table-wrap">
                  <table className="class-custom-problems__table">
                    <thead>
                      <tr>
                        <th>{t('classEdit.customProblemsColTitle')}</th>
                        <th>{t('classEdit.customProblemsColDifficulty')}</th>
                        <th>{t('classEdit.customProblemsColTopic')}</th>
                        <th>{t('classEdit.customProblemsColLessons')}</th>
                        <th>{t('classEdit.memberTableActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customProblemsList.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <div className="class-custom-problems__title-cell">
                              <Link
                                to={problemPath(p)}
                                className="class-custom-problems__link"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {p.title}
                              </Link>
                            </div>
                          </td>
                          <td>
                            <span style={{ color: getDifficultyColor(p.difficulty), fontWeight: 600 }}>
                              {getDifficultyLabel(p.difficulty)}
                            </span>
                          </td>
                          <td>{p.topic || '—'}</td>
                          <td>
                            <span className="class-custom-problems__lessons">
                              {p.lessons.length > 0
                                ? p.lessons.map((l) => l.title).join(', ')
                                : t('classEdit.customProblemsNotInLessons')}
                            </span>
                          </td>
                          <td>
                            <div className="class-custom-problems__actions">
                              <button
                                type="button"
                                className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                                title={t('classEdit.customProblemsEdit')}
                                onClick={() =>
                                  navigate(`/problems/create?classId=${classData.id}&problemId=${p.id}`)
                                }
                              >
                                <FaEdit />
                              </button>
                              <Link
                                to={problemPath(p)}
                                className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                                title={t('classEdit.viewProblem')}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaEye />
                              </Link>
                              <button
                                type="button"
                                className="class-edit-btn class-edit-btn-icon class-edit-btn-sm"
                                title={t('classEdit.customProblemsCopyLink')}
                                onClick={async () => {
                                  try {
                                    const url = `${window.location.origin}${problemPath(p)}`;
                                    await navigator.clipboard.writeText(url);
                                    setCopyToast(true);
                                    window.setTimeout(() => setCopyToast(false), 2000);
                                  } catch {
                                    /* ignore */
                                  }
                                }}
                              >
                                <FaLink />
                              </button>
                              <button
                                type="button"
                                className="class-edit-btn class-edit-btn-icon class-edit-btn-sm class-edit-btn-danger"
                                title={t('classEdit.customProblemsDelete')}
                                onClick={() =>
                                  setCustomProblemModal({ id: p.id, title: p.title })
                                }
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {activeTab === 'students' && (
            <div className="class-students">
              <div className="class-students__stats">
                <div className="class-students__stat">
                  <div className="class-students__stat-icon class-students__stat-icon--total"><FaUsers /></div>
                  <div>
                    <div className="class-students__stat-label">{t('classEdit.studentsStatTotal')}</div>
                    <div className="class-students__stat-value">{studentsTabStats.total}</div>
                    <div className="class-students__stat-hint">{t('classEdit.studentsStatTotalHint')}</div>
                  </div>
                </div>
                <div className="class-students__stat">
                  <div className="class-students__stat-icon class-students__stat-icon--active"><FaCheck /></div>
                  <div>
                    <div className="class-students__stat-label">{t('classEdit.studentsStatActive')}</div>
                    <div className="class-students__stat-value">
                      {studentsTabStats.active}
                      {studentsTabStats.total > 0 && (
                        <span>
                          {' '}({Math.round((studentsTabStats.active / studentsTabStats.total) * 100)}%)
                        </span>
                      )}
                    </div>
                    <div className="class-students__stat-hint">{t('classEdit.studentsStatActiveHint')}</div>
                  </div>
                </div>
                <div className="class-students__stat">
                  <div className="class-students__stat-icon class-students__stat-icon--new"><FaUserPlus /></div>
                  <div>
                    <div className="class-students__stat-label">{t('classEdit.studentsStatNew')}</div>
                    <div className="class-students__stat-value">{studentsTabStats.newWeek}</div>
                    <div className="class-students__stat-hint">{t('classEdit.studentsStatNewHint')}</div>
                  </div>
                </div>
                <div className="class-students__stat">
                  <div
                    className="class-students__stat-ring"
                    style={{ ['--ring' as string]: `${avgProgress}` }}
                    aria-hidden
                  >
                    <span>{avgProgress}%</span>
                  </div>
                  <div>
                    <div className="class-students__stat-label">{t('classEdit.studentsStatProgress')}</div>
                    <div className="class-students__stat-hint">{t('classEdit.studentsStatProgressHint')}</div>
                  </div>
                </div>
                <div className="class-students__stat">
                  <div className="class-students__stat-icon class-students__stat-icon--inactive"><FaUserClock /></div>
                  <div>
                    <div className="class-students__stat-label">{t('classEdit.studentsStatInactive')}</div>
                    <div className="class-students__stat-value">{studentsTabStats.inactive}</div>
                    <div className="class-students__stat-hint class-students__stat-hint--warn">
                      {t('classEdit.studentsStatInactiveHint')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="class-dash__grid class-students__grid">
                <section className="class-dash__panel class-students__main">
                  <div className="class-students__filters">
                    <label className="class-dash__search class-students__search">
                      <FaSearch aria-hidden />
                      <input
                        type="search"
                        value={studentsSearch}
                        onChange={(e) => setStudentsSearch(e.target.value)}
                        placeholder={t('classEdit.studentsSearchPlaceholder')}
                        aria-label={t('classEdit.studentsSearchPlaceholder')}
                      />
                    </label>
                    <select
                      className="class-edit-input class-students__select"
                      value={studentsStatusFilter}
                      onChange={(e) =>
                        setStudentsStatusFilter(
                          e.target.value as 'all' | 'active' | 'inactive' | 'at_risk' | 'missing',
                        )
                      }
                      aria-label={t('classEdit.studentsFilterStatus')}
                    >
                      <option value="all">{t('classEdit.studentsFilterStatusAll')}</option>
                      <option value="active">{t('classEdit.studentStatusActive')}</option>
                      <option value="at_risk">{t('classEdit.studentStatusAtRisk')}</option>
                      <option value="inactive">{t('classEdit.studentStatusInactive')}</option>
                      {canEdit ? (
                        <option value="missing">{t('classEdit.studentStatusMissing')}</option>
                      ) : null}
                    </select>
                    <select
                      className="class-edit-input class-students__select"
                      value={studentsSort}
                      onChange={(e) => setStudentsSort(e.target.value as 'activity' | 'progress' | 'name')}
                      aria-label={t('classEdit.studentsSort')}
                    >
                      <option value="activity">{t('classEdit.studentsSortActivity')}</option>
                      <option value="progress">{t('classEdit.studentsSortProgress')}</option>
                      <option value="name">{t('classEdit.studentsSortName')}</option>
                    </select>
                  </div>

                  <div className="class-students__toolbar">
                    <div className="class-students-export" ref={studentsExportMenuRef}>
                      <button
                        type="button"
                        className="class-edit-btn class-edit-btn-outline class-edit-btn-sm class-students-export__toggle"
                        onClick={() => setStudentsExportMenuOpen((v) => !v)}
                        disabled={filteredStudentsList.length === 0}
                        aria-expanded={studentsExportMenuOpen}
                        aria-haspopup="menu"
                      >
                        <FaDownload /> {t('classEdit.analyticsExport')}
                        <FaChevronDown className="class-students-export__chevron" aria-hidden />
                      </button>
                      {studentsExportMenuOpen && (
                        <div className="class-students-export__menu" role="menu">
                          {(['xlsx', 'csv', 'json'] as const).map((format) => (
                            <button
                              key={format}
                              type="button"
                              className="class-students-export__item"
                              role="menuitem"
                              onClick={() => {
                                exportStudents(format);
                                setStudentsExportMenuOpen(false);
                              }}
                            >
                              <span className="class-students-export__format">{format.toUpperCase()}</span>
                              <span className="class-students-export__hint">
                                {t(`classEdit.exportFormat.${format}`)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="class-students-table-wrap">
                    {filteredStudentsList.length === 0 ? (
                      <p className="class-edit-invite-hint">{t('classEdit.studentsEmpty')}</p>
                    ) : (
                      <table className="class-students-table">
                        <thead>
                          <tr>
                            <th>{t('classEdit.studentsColStudent')}</th>
                            <th>{t('classEdit.studentsColNickname')}</th>
                            <th>{t('classEdit.studentsColProgress')}</th>
                            <th>{t('classEdit.studentsColSolved')}</th>
                            <th>{t('classEdit.studentsColActivity')}</th>
                            <th>{t('classEdit.studentsColStatus')}</th>
                            <th>{t('classEdit.memberTableActions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedStudents.map((m) => {
                            const status = getMemberStatus(m);
                            const progress = memberProgress(m);
                            return (
                              <tr key={m.id}>
                                <td>
                                  {canEdit ? (
                                    <button
                                      type="button"
                                      className="class-students-user"
                                      onClick={() => openStudentWorkbook(m.user_id)}
                                      title={t('classEdit.openStudentWork')}
                                    >
                                      <span className="class-students-user__avatar">
                                        {m.avatar ? <img src={m.avatar} alt="" /> : memberInitials(m)}
                                      </span>
                                      <span className="class-students-user__name">{memberShortName(m)}</span>
                                    </button>
                                  ) : m.username ? (
                                    <Link
                                      to={`/profile/${encodeURIComponent(m.username)}`}
                                      className="class-students-user"
                                      title={t('classEdit.studentsViewProfile')}
                                    >
                                      <span className="class-students-user__avatar">
                                        {m.avatar ? <img src={m.avatar} alt="" /> : memberInitials(m)}
                                      </span>
                                      <span className="class-students-user__name">{memberShortName(m)}</span>
                                    </Link>
                                  ) : (
                                    <div className="class-students-user">
                                      <span className="class-students-user__avatar">
                                        {m.avatar ? <img src={m.avatar} alt="" /> : memberInitials(m)}
                                      </span>
                                      <span className="class-students-user__name">{memberShortName(m)}</span>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="class-students-nick">
                                    {m.username ? (
                                      <Link
                                        to={`/profile/${encodeURIComponent(m.username)}`}
                                        className="class-students-nick__user"
                                        title={t('classEdit.studentsViewProfile')}
                                      >
                                        @{m.username}
                                      </Link>
                                    ) : (
                                      <span className="class-students-nick__user">—</span>
                                    )}
                                    {m.email ? (
                                      <span className="class-students-nick__email" title={m.email}>{m.email}</span>
                                    ) : null}
                                  </div>
                                </td>
                                <td>
                                  <div className="class-students-progress">
                                    <span>{progress}%</span>
                                    <div className="class-students-progress__bar">
                                      <div
                                        className="class-students-progress__fill"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td className="class-students-solved">
                                  {m.solved_count ?? 0} / {totalProblems}
                                </td>
                                <td className="class-students-activity">
                                  {formatMemberActivity(memberActivityRef(m))}
                                </td>
                                <td>
                                  {canEdit && status === 'at_risk' ? (
                                    <button
                                      type="button"
                                      className={`class-students-status class-students-status--${status}`}
                                      onClick={() => openStudentWorkbook(m.user_id)}
                                      title={t('classEdit.openStudentWork')}
                                    >
                                      {memberStatusLabel(status)}
                                    </button>
                                  ) : (
                                    <span className={`class-students-status class-students-status--${status}`}>
                                      {memberStatusLabel(status)}
                                    </span>
                                  )}
                                  {assignmentProgress?.missingUserIds.includes(Number(m.user_id)) ? (
                                    <span className="class-students-status class-students-status--missing">
                                      {t('classEdit.studentStatusMissing')}
                                    </span>
                                  ) : null}
                                </td>
                                <td>
                                  <div className="class-students-actions">
                                    {canEdit ? (
                                      <button
                                        type="button"
                                        className="class-analytics-icon-btn"
                                        onClick={() => openStudentWorkbook(m.user_id)}
                                        title={t('classEdit.openStudentWork')}
                                        aria-label={t('classEdit.openStudentWork')}
                                      >
                                        <FaChartBar />
                                      </button>
                                    ) : null}
                                    {m.username ? (
                                      <Link
                                        to={`/profile/${encodeURIComponent(m.username)}`}
                                        className="class-analytics-icon-btn"
                                        title={t('classEdit.studentsViewProfile')}
                                        aria-label={t('classEdit.studentsViewProfile')}
                                      >
                                        <FaEye />
                                      </Link>
                                    ) : null}
                                    {canEdit && (
                                      <button
                                        type="button"
                                        className="class-analytics-icon-btn class-students-actions__danger"
                                        onClick={() =>
                                          setRemoveMemberModal({
                                            userId: m.user_id,
                                            username: m.username ?? `ID ${m.user_id}`,
                                          })
                                        }
                                        title={t('classEdit.removeFromClass')}
                                        aria-label={t('classEdit.removeFromClass')}
                                      >
                                        <FaTrash />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {filteredStudentsList.length > 0 && (
                    <div className="class-students-pager">
                      <span className="class-students-pager__info">
                        {t('classEdit.studentsShowBy').replace('{count}', String(studentsPageSize))}
                      </span>
                      <div className="class-students-pager__pages">
                        <button
                          type="button"
                          className="class-edit-btn class-edit-btn-sm"
                          disabled={studentsPage <= 1}
                          onClick={() => setStudentsPage((p) => Math.max(1, p - 1))}
                        >
                          ‹
                        </button>
                        {Array.from({ length: studentsTotalPages }, (_, i) => i + 1)
                          .filter((p) => p === 1 || p === studentsTotalPages || Math.abs(p - studentsPage) <= 1)
                          .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                            if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) =>
                            p === '…' ? (
                              <span key={`e-${idx}`} className="class-students-pager__ellipsis">…</span>
                            ) : (
                              <button
                                key={p}
                                type="button"
                                className={`class-edit-btn class-edit-btn-sm${studentsPage === p ? ' is-active' : ''}`}
                                onClick={() => setStudentsPage(p)}
                              >
                                {p}
                              </button>
                            )
                          )}
                        <button
                          type="button"
                          className="class-edit-btn class-edit-btn-sm"
                          disabled={studentsPage >= studentsTotalPages}
                          onClick={() => setStudentsPage((p) => Math.min(studentsTotalPages, p + 1))}
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}
                </section>

                <aside className="class-dash__widgets class-students__aside">
                  <div className="class-dash__widget class-students-invite">
                    <h3 className="class-dash__widget-title">
                      <FaLink aria-hidden /> {t('classEdit.studentsInviteTitle')}
                    </h3>
                    <p className="class-students-invite__hint">{t('classEdit.studentsInviteHint')}</p>
                    <div className="class-students-invite__code">
                      <code>{`${typeof window !== 'undefined' ? window.location.origin : ''}/join-class?code=${classData.invite_code}`}</code>
                    </div>
                    {canEdit ? (
                      <div className="class-students-invite__actions">
                        <button type="button" className="class-edit-btn class-edit-btn-primary" onClick={copyInviteLink}>
                          <FaCopy /> {t('classEdit.copyLink')}
                        </button>
                        <form onSubmit={handleInvite} className="class-students-invite__form">
                          <input
                            type="text"
                            value={inviteInput}
                            onChange={(e) => setInviteInput(e.target.value)}
                            placeholder={t('classEdit.invitePlaceholder')}
                            className="class-edit-input"
                          />
                          <button
                            type="submit"
                            disabled={inviteLoading || !inviteInput.trim()}
                            className="class-edit-btn class-edit-btn-outline"
                          >
                            {inviteLoading ? '…' : t('classEdit.inviteButton')}
                          </button>
                        </form>
                        {inviteSuccess && <div className="class-edit-success">{inviteSuccess}</div>}
                        {copyToast && <div className="class-edit-toast">{t('classEdit.linkCopied')}</div>}
                      </div>
                    ) : (
                      <p className="class-edit-invite-hint">{t('classEdit.inviteHint')}</p>
                    )}
                  </div>

                  {renderActivityWidget()}
                </aside>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && canEdit && (
            <section className="class-dash__panel class-analytics">
              <h2 className="class-dash__panel-title class-analytics__title">
                {t('classEdit.analyticsByLessons')}
              </h2>
              {classData.lessons.length === 0 ? (
                <p className="class-edit-invite-hint">{t('classEdit.analyticsEmptyHint')}</p>
              ) : (
                <>
                  {!analyticsLoading && !analyticsError && lessonAnalytics && (
                    <div className="class-analytics__stats">
                      <div className="class-analytics__stat class-analytics__stat--progress">
                        <div
                          className="class-analytics__ring"
                          style={{ ['--ring' as string]: `${analyticsStats.progress}` }}
                          aria-hidden
                        >
                          <span>{analyticsStats.progress}%</span>
                        </div>
                        <div className="class-analytics__stat-body">
                          <div className="class-analytics__stat-label">{t('classEdit.analyticsOverallProgress')}</div>
                          <div className="class-analytics__stat-hint">{t('classEdit.analyticsClassAverage')}</div>
                        </div>
                      </div>
                      <div className="class-analytics__stat">
                        <div className="class-analytics__stat-icon class-analytics__stat-icon--users">
                          <FaUsers />
                        </div>
                        <div className="class-analytics__stat-body">
                          <div className="class-analytics__stat-label">{t('classEdit.analyticsActiveStudents')}</div>
                          <div className="class-analytics__stat-value">
                            {analyticsStats.active}
                            <span> / {analyticsStats.totalStudents}</span>
                          </div>
                          <div className="class-analytics__stat-hint">
                            {analyticsPeriod === '7d'
                              ? t('classEdit.analyticsPeriod7dHint')
                              : analyticsPeriod === '30d'
                                ? t('classEdit.analyticsPeriod30dHint')
                                : t('classEdit.analyticsPeriodAllHint')}
                          </div>
                        </div>
                      </div>
                      <div className="class-analytics__stat">
                        <div className="class-analytics__stat-icon class-analytics__stat-icon--solved">
                          <FaCheck />
                        </div>
                        <div className="class-analytics__stat-body">
                          <div className="class-analytics__stat-label">{t('classEdit.analyticsTasksSolved')}</div>
                          <div className="class-analytics__stat-value">
                            {analyticsStats.solved}
                            <span> / {analyticsStats.totalCells}</span>
                          </div>
                          <div className="class-analytics__stat-hint">
                            {analyticsStats.totalCells > 0
                              ? t('classEdit.analyticsSolvedPct').replace(
                                  '{pct}',
                                  String(Math.round((analyticsStats.solved / analyticsStats.totalCells) * 100))
                                )
                              : '—'}
                          </div>
                        </div>
                      </div>
                      <div className="class-analytics__stat">
                        <div className="class-analytics__stat-icon class-analytics__stat-icon--review">
                          <FaClock />
                        </div>
                        <div className="class-analytics__stat-body">
                          <div className="class-analytics__stat-label">{t('classEdit.analyticsUnderReview')}</div>
                          <div className="class-analytics__stat-value">{analyticsStats.review}</div>
                          <div className="class-analytics__stat-hint class-analytics__stat-hint--warn">
                            {t('classEdit.analyticsNeedsAttention')}
                          </div>
                        </div>
                      </div>
                      <div className="class-analytics__stat">
                        <div className="class-analytics__stat-icon class-analytics__stat-icon--error">
                          <FaExclamationTriangle />
                        </div>
                        <div className="class-analytics__stat-body">
                          <div className="class-analytics__stat-label">{t('classEdit.analyticsWithErrors')}</div>
                          <div className="class-analytics__stat-value">{analyticsStats.errors}</div>
                          <div className="class-analytics__stat-hint class-analytics__stat-hint--error">
                            {t('classEdit.analyticsNeedsHelp')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {assignmentProgress
                    && selectedLessonIdForAnalytics === assignmentProgress.lessonId
                    && assignmentProgress.missingUserIds.length > 0 && (
                    <div className="class-analytics__missing-banner">
                      <p>
                        {t('classEdit.assignmentAnalyticsBanner').replace(
                          '{count}',
                          String(assignmentProgress.missingUserIds.length),
                        )}
                      </p>
                      <button
                        type="button"
                        className="class-edit-btn class-edit-btn-sm"
                        onClick={() => setAnalyticsStudentFilter(assignmentProgress.missingUserIds)}
                      >
                        {t('classEdit.assignmentFilterMissing')}
                      </button>
                    </div>
                  )}

                  <div className="class-analytics-filters">
                    <div className="class-analytics-field">
                      <span className="class-analytics-field__label">{t('classEdit.lessonLabel')}</span>
                      <select
                        className="class-edit-input class-analytics-select"
                        value={selectedLessonIdForAnalytics ?? classData.lessons[0].id}
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          setSelectedLessonIdForAnalytics(id);
                          setAnalyticsStudentFilter([]);
                          setAnalyticsStudentSearch('');
                          setAnalyticsStudentMenuOpen(false);
                          fetchLessonAnalytics(id);
                        }}
                      >
                        {classData.lessons.map((l, idx) => (
                          <option key={l.id} value={l.id}>{idx + 1}. {l.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="class-analytics-field">
                      <span className="class-analytics-field__label">{t('classEdit.moduleLabel')}</span>
                      <select className="class-edit-input class-analytics-select" disabled value="all">
                        <option value="all">{t('classEdit.moduleAll')}</option>
                      </select>
                    </div>

                    {lessonAnalytics && lessonAnalytics.students.length > 0 && (
                      <div className="class-analytics-field class-analytics-student-filter" ref={analyticsStudentMenuRef}>
                        <span className="class-analytics-field__label">{t('classEdit.studentsFilterLabel')}</span>
                        <button
                          type="button"
                          className={`class-analytics-student-trigger${analyticsStudentMenuOpen ? ' is-open' : ''}`}
                          aria-haspopup="listbox"
                          aria-expanded={analyticsStudentMenuOpen}
                          onClick={() => setAnalyticsStudentMenuOpen((open) => !open)}
                        >
                          <span className="class-analytics-student-trigger__text">
                            {analyticsStudentFilter.length === 0
                              ? t('classEdit.studentsFilterAll')
                              : t('classEdit.studentsFilterSelected').replace(
                                  '{count}',
                                  String(analyticsStudentFilter.length)
                                )}
                          </span>
                          <FaChevronDown aria-hidden />
                        </button>
                        {analyticsStudentMenuOpen && (
                          <div className="class-analytics-student-menu" role="listbox" aria-multiselectable="true">
                            <div className="class-analytics-student-menu__actions">
                              <button
                                type="button"
                                className="class-analytics-student-menu__link"
                                onClick={() =>
                                  setAnalyticsStudentFilter(lessonAnalytics.students.map((s) => s.userId))
                                }
                              >
                                {t('classEdit.studentsFilterSelectAll')}
                              </button>
                              <button
                                type="button"
                                className="class-analytics-student-menu__link"
                                onClick={() => setAnalyticsStudentFilter([])}
                              >
                                {t('classEdit.studentsFilterClear')}
                              </button>
                            </div>
                            <ul className="class-analytics-student-menu__list">
                              {lessonAnalytics.students.map((s) => {
                                const checked = analyticsStudentFilter.includes(s.userId);
                                const label = studentDisplayName(s);
                                return (
                                  <li key={s.userId}>
                                    <label className={`class-analytics-student-option${checked ? ' is-checked' : ''}`}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleAnalyticsStudent(s.userId)}
                                      />
                                      <span className="class-analytics-student-option__box" aria-hidden>
                                        {checked ? <FaCheck /> : null}
                                      </span>
                                      <span className="class-analytics-student-option__name" title={label}>{label}</span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="class-analytics-field class-analytics-field--search">
                      <span className="class-analytics-field__label">{t('classEdit.analyticsSearchLabel')}</span>
                      <div className="class-analytics-search">
                        <FaSearch aria-hidden />
                        <input
                          type="search"
                          className="class-edit-input"
                          placeholder={t('classEdit.analyticsSearchPlaceholder')}
                          value={analyticsStudentSearch}
                          onChange={(e) => setAnalyticsStudentSearch(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="class-analytics-field">
                      <span className="class-analytics-field__label">{t('classEdit.analyticsPeriodLabel')}</span>
                      <div className="class-analytics-period">
                        <FaCalendarAlt aria-hidden />
                        <select
                          className="class-edit-input class-analytics-select"
                          value={analyticsPeriod}
                          onChange={(e) => setAnalyticsPeriod(e.target.value as 'all' | '7d' | '30d')}
                        >
                          <option value="all">{t('classEdit.analyticsPeriodAll')}</option>
                          <option value="7d">{t('classEdit.analyticsPeriod7d')}</option>
                          <option value="30d">{t('classEdit.analyticsPeriod30d')}</option>
                        </select>
                      </div>
                    </div>

                    <div className="class-analytics-field class-analytics-field--export">
                      <span className="class-analytics-field__label">&nbsp;</span>
                      <button
                        type="button"
                        className="class-edit-btn class-analytics-export"
                        onClick={exportAnalyticsCsv}
                        disabled={!lessonAnalytics || filteredAnalyticsStudents.length === 0}
                      >
                        <FaDownload />
                        {t('classEdit.analyticsExport')}
                      </button>
                    </div>
                  </div>

                  <div className="class-analytics-legend" aria-hidden>
                    <span className="class-analytics-legend__item">
                      <i className="class-analytics-dot class-analytics-dot--solved" />
                      {t('classEdit.solved')}
                    </span>
                    <span className="class-analytics-legend__item">
                      <i className="class-analytics-dot class-analytics-dot--review" />
                      {t('classEdit.statusReview')}
                    </span>
                    <span className="class-analytics-legend__item">
                      <i className="class-analytics-dot class-analytics-dot--error" />
                      {t('classEdit.statusError')}
                    </span>
                    <span className="class-analytics-legend__item">
                      <i className="class-analytics-dot class-analytics-dot--empty" />
                      {t('classEdit.statusNotStarted')}
                    </span>
                  </div>

                  {analyticsLoading && <div className="class-edit-loading">{t('classEdit.loadingAnalytics')}</div>}
                  {analyticsError && !analyticsLoading && (
                    <div className="class-edit-error" role="alert">{analyticsError}</div>
                  )}
                  {!analyticsLoading && !analyticsError && lessonAnalytics && (
                    <div className="class-analytics-table-wrap">
                      {filteredAnalyticsStudents.length === 0 ? (
                        <p className="class-edit-invite-hint">{t('classEdit.studentsFilterEmpty')}</p>
                      ) : (
                        <table className="class-analytics-table">
                          <thead>
                            <tr>
                              <th className="class-analytics-table__student-col">{t('classEdit.studentsColumn')}</th>
                              {lessonAnalytics.problems.map((p) => {
                                const rate = problemSolveRates.get(p.problemId) ?? 0;
                                return (
                                  <th key={p.problemId}>
                                    <div className="class-analytics-colhead">
                                      <span className="class-analytics-colhead__title" title={p.title}>{p.title}</span>
                                      <div className="class-analytics-colhead__bar">
                                        <div className="class-analytics-colhead__fill" style={{ width: `${rate}%` }} />
                                      </div>
                                      <div className="class-analytics-colhead__foot">
                                        <span className="class-analytics-colhead__pct">{rate}%</span>
                                        <Link
                                          to={`/problems/${p.problemId}`}
                                          className="class-analytics-icon-btn class-analytics-colhead__open"
                                          title={t('classEdit.viewProblem')}
                                          aria-label={t('classEdit.viewProblem')}
                                        >
                                          <FaEye />
                                        </Link>
                                      </div>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAnalyticsStudents.map((s) => (
                              <tr
                                key={s.userId}
                                className={
                                  assignmentProgress
                                  && selectedLessonIdForAnalytics === assignmentProgress.lessonId
                                  && assignmentProgress.missingUserIds.includes(s.userId)
                                    ? 'class-analytics-row--missing'
                                    : undefined
                                }
                              >
                                <td className="class-analytics-table__student-col">
                                  {s.username ? (
                                    <Link
                                      to={`/profile/${encodeURIComponent(s.username)}`}
                                      className="class-analytics-student"
                                      title={t('classEdit.studentsViewProfile')}
                                    >
                                      <span className="class-analytics-student__avatar">
                                        {s.avatar ? (
                                          <img src={s.avatar} alt="" />
                                        ) : (
                                          studentInitials(s)
                                        )}
                                      </span>
                                      <span className="class-analytics-student__meta">
                                        <span className="class-analytics-student__name">{studentShortName(s)}</span>
                                        <span className="class-analytics-student__handle">@{s.username}</span>
                                      </span>
                                    </Link>
                                  ) : (
                                    <div className="class-analytics-student">
                                      <span className="class-analytics-student__avatar">
                                        {s.avatar ? (
                                          <img src={s.avatar} alt="" />
                                        ) : (
                                          studentInitials(s)
                                        )}
                                      </span>
                                      <span className="class-analytics-student__meta">
                                        <span className="class-analytics-student__name">{studentShortName(s)}</span>
                                      </span>
                                    </div>
                                  )}
                                </td>
                                {lessonAnalytics.problems.map((p) => {
                                  const sub = lessonAnalytics.submissions.find(
                                    (x) => x.userId === s.userId && x.problemId === p.problemId
                                  );
                                  const visible = sub && submissionInPeriod(sub) ? sub : undefined;
                                  const status = getAnalyticsStatus(visible?.status);
                                  if (!visible) {
                                    return (
                                      <td key={p.problemId} className="class-analytics-cell class-analytics-cell--empty">
                                        <div className="class-analytics-cell-inner">
                                          <span className="class-analytics-badge class-analytics-badge--empty">
                                            {analyticsStatusLabel('empty')}
                                          </span>
                                          <span className="class-analytics-empty-dash">—</span>
                                        </div>
                                      </td>
                                    );
                                  }
                                  return (
                                    <td key={p.problemId} className={`class-analytics-cell class-analytics-cell--${status}`}>
                                      <div className="class-analytics-cell-inner">
                                        <span className={`class-analytics-badge class-analytics-badge--${status}`}>
                                          {analyticsStatusLabel(status)}
                                        </span>
                                        <div className="class-analytics-cell-meta">
                                          <span className="class-analytics-meta-text">
                                            {formatAnalyticsDate(visible.submitted_at)}
                                            <span className="class-analytics-meta-sep">·</span>
                                            <span className="class-analytics-lang">{visible.language}</span>
                                          </span>
                                          <div className="class-analytics-cell-actions">
                                            {visible.feedback ? (
                                              <button
                                                type="button"
                                                className={`class-analytics-icon-btn class-analytics-feedback-badge is-${
                                                  visible.feedback.status === 'replied' && visible.feedback.unread_reply
                                                    ? 'unread-reply'
                                                    : visible.feedback.status
                                                }`}
                                                onClick={() => openAnalyticsCodeModal(visible)}
                                                title={feedbackBadgeTitle(visible.feedback)}
                                                aria-label={feedbackBadgeTitle(visible.feedback)}
                                              >
                                                <FaCommentDots />
                                              </button>
                                            ) : null}
                                            <button
                                              type="button"
                                              className="class-analytics-icon-btn"
                                              onClick={() => openAnalyticsCodeModal(visible)}
                                              title={t('classEdit.showCode')}
                                              aria-label={t('classEdit.showCodeAria')}
                                            >
                                              <FaCode />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {activeTab === 'settings' && canEdit && (
            <section className="class-dash__panel">
              <h2 className="class-dash__panel-title" style={{ marginBottom: 16 }}>{t('classEdit.settingsTab')}</h2>
              <p className="class-edit-invite-hint">{t('classEdit.settingsNameHint')}</p>
              <div className="class-dash__settings-row">
                {editingName ? (
                  <>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveName();
                        if (e.key === 'Escape') { setEditingName(false); setEditName(classData.name); }
                      }}
                      disabled={savingName}
                      className="class-edit-name-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleSaveName}
                      disabled={savingName || editName.trim() === classData.name}
                      className="class-edit-btn class-edit-btn-sm"
                    >
                      {savingName ? '…' : <FaCheck />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingName(false); setEditName(classData.name); }}
                      disabled={savingName}
                      className="class-edit-btn class-edit-btn-sm"
                    >
                      <FaTimes />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="class-dash__hero-title" style={{ fontSize: '1.25rem' }}>{classData.name}</span>
                    <button
                      type="button"
                      onClick={() => { setEditingName(true); setEditName(classData.name); }}
                      className="class-edit-btn class-edit-btn-icon"
                      title={t('classEdit.editNameTitle')}
                    >
                      <FaEdit />
                    </button>
                  </>
                )}
              </div>

              <div className="class-dash__settings-danger">
                <h3 className="class-dash__settings-danger-title">{t('classEdit.deleteClass')}</h3>
                <p className="class-edit-invite-hint">{t('classEdit.deleteClassHint')}</p>
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-danger"
                  onClick={() => {
                    setDeleteClassConfirmName('');
                    setDeleteClassModalOpen(true);
                  }}
                >
                  <FaTrash /> {t('classEdit.deleteClass')}
                </button>
              </div>
            </section>
          )}

          {error && (
            <div className="class-edit-error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>

      {copyToast && activeTab === 'customProblems' && (
        <div className="class-edit-toast class-edit-toast--fixed">{t('classEdit.linkCopied')}</div>
      )}

      {createClassModalOpen && (
        <div
          className="class-edit-modal-overlay"
          onClick={() => setCreateClassModalOpen(false)}
          role="presentation"
        >
          <div
            className="class-edit-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-class-modal-title"
          >
            <h2 id="create-class-modal-title" className="class-edit-confirm-modal-title">
              {t('classEdit.createClass')}
            </h2>
            <p className="class-edit-confirm-modal-text">{t('classEdit.createClassDesc')}</p>
            {error && <div className="class-edit-error" role="alert">{error}</div>}
            <form onSubmit={handleCreateClass}>
              <input
                type="text"
                value={createClassModalName}
                onChange={(e) => setCreateClassModalName(e.target.value)}
                placeholder={t('classEdit.classNamePlaceholder')}
                maxLength={200}
                className="class-edit-input"
                autoFocus
              />
              <div className="class-edit-confirm-modal-actions" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-sm"
                  onClick={() => setCreateClassModalOpen(false)}
                >
                  {t('classEdit.cancel')}
                </button>
                <button
                  type="submit"
                  className="class-edit-btn class-edit-btn-primary class-edit-btn-sm"
                  disabled={createClassLoading || !createClassModalName.trim()}
                >
                  {createClassLoading ? t('classEdit.creating') : t('classEdit.createClassButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteClassModalOpen && classData && (
        <div
          className="class-edit-modal-overlay"
          onClick={() => {
            setDeleteClassModalOpen(false);
            setDeleteClassConfirmName('');
          }}
          role="presentation"
        >
          <div
            className="class-edit-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-class-modal-title"
          >
            <h2 id="delete-class-modal-title" className="class-edit-confirm-modal-title">
              {t('classEdit.deleteClassConfirmTitle')}
            </h2>
            <p className="class-edit-confirm-modal-text">
              {t('classEdit.deleteClassConfirmText').replace('{name}', classData.name)}
            </p>
            <div className="class-edit-confirm-modal-field">
              <label className="class-edit-invite-hint" htmlFor="delete-class-confirm-name">
                {t('classEdit.deleteClassNameLabel')}
              </label>
              <input
                id="delete-class-confirm-name"
                type="text"
                value={deleteClassConfirmName}
                onChange={(e) => setDeleteClassConfirmName(e.target.value)}
                className="class-edit-input"
                autoFocus
              />
            </div>
            <div className="class-edit-confirm-modal-actions">
              <button
                type="button"
                className="class-edit-btn class-edit-btn-sm"
                onClick={() => {
                  setDeleteClassModalOpen(false);
                  setDeleteClassConfirmName('');
                }}
              >
                {t('classEdit.cancel')}
              </button>
              <button
                type="button"
                className="class-edit-btn class-edit-btn-danger class-edit-btn-sm"
                disabled={deleteClassLoading || deleteClassConfirmName.trim() !== classData.name}
                onClick={handleDeleteClass}
              >
                {deleteClassLoading ? '…' : t('classEdit.deleteClassConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeMemberModal && (
        <div className="class-edit-modal-overlay" onClick={() => setRemoveMemberModal(null)} role="presentation">
          <div className="class-edit-confirm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="confirm-remove-member-title">
            <h2 id="confirm-remove-member-title" className="class-edit-confirm-modal-title">{t('classEdit.confirmRemoveMemberTitle')}</h2>
            <p className="class-edit-confirm-modal-text">
              {t('classEdit.confirmRemoveMemberText').replace('{name}', removeMemberModal.username)}
            </p>
            <div className="class-edit-confirm-modal-actions">
              <button type="button" className="class-edit-btn class-edit-btn-sm" onClick={() => setRemoveMemberModal(null)}>
                {t('classEdit.confirmRemoveCancel')}
              </button>
              <button
                type="button"
                className="class-edit-btn class-edit-btn-danger class-edit-btn-sm"
                onClick={() => {
                  handleRemoveMember(removeMemberModal.userId);
                  setRemoveMemberModal(null);
                }}
              >
                {t('classEdit.confirmRemoveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteLessonModal && (
        <div className="class-edit-modal-overlay" onClick={() => setDeleteLessonModal(null)} role="presentation">
          <div className="class-edit-confirm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="confirm-delete-lesson-title">
            <h2 id="confirm-delete-lesson-title" className="class-edit-confirm-modal-title">{t('classEdit.confirmDeleteLessonTitle')}</h2>
            <p className="class-edit-confirm-modal-text">
              {t('classEdit.confirmDeleteLessonText').replace('{title}', deleteLessonModal.title)}
            </p>
            <div className="class-edit-confirm-modal-actions">
              <button type="button" className="class-edit-btn class-edit-btn-sm" onClick={() => setDeleteLessonModal(null)}>
                {t('classEdit.confirmRemoveCancel')}
              </button>
              <button
                type="button"
                className="class-edit-btn class-edit-btn-danger class-edit-btn-sm"
                onClick={() => handleDeleteLesson(deleteLessonModal.id)}
              >
                {t('classEdit.confirmRemoveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeProblemModal && (
        <div className="class-edit-modal-overlay" onClick={() => setRemoveProblemModal(null)} role="presentation">
          <div className="class-edit-confirm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="confirm-remove-problem-title">
            <h2 id="confirm-remove-problem-title" className="class-edit-confirm-modal-title">{t('classEdit.confirmRemoveProblemTitle')}</h2>
            <p className="class-edit-confirm-modal-text">
              {t('classEdit.confirmRemoveProblemText').replace('{title}', removeProblemModal.title)}
            </p>
            <div className="class-edit-confirm-modal-actions">
              <button type="button" className="class-edit-btn class-edit-btn-sm" onClick={() => setRemoveProblemModal(null)}>
                {t('classEdit.confirmRemoveCancel')}
              </button>
              <button
                type="button"
                className="class-edit-btn class-edit-btn-danger class-edit-btn-sm"
                onClick={() =>
                  handleRemoveProblemFromLesson(removeProblemModal.lessonId, removeProblemModal.problemId)
                }
              >
                {t('classEdit.confirmRemoveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {customProblemModal && (
        <div className="class-edit-modal-overlay" onClick={() => setCustomProblemModal(null)} role="presentation">
          <div
            className="class-edit-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-custom-problem-title"
          >
            <h2 id="confirm-custom-problem-title" className="class-edit-confirm-modal-title">
              {t('classEdit.customProblemsDeleteTitle')}
            </h2>
            <p className="class-edit-confirm-modal-text">
              {t('classEdit.customProblemsDeleteText').replace('{title}', customProblemModal.title)}
            </p>
            <div className="class-edit-confirm-modal-actions">
              <button
                type="button"
                className="class-edit-btn class-edit-btn-sm"
                onClick={() => setCustomProblemModal(null)}
              >
                {t('classEdit.confirmRemoveCancel')}
              </button>
              <button
                type="button"
                className="class-edit-btn class-edit-btn-danger class-edit-btn-sm"
                onClick={() => handleDeleteCustomProblem(customProblemModal.id)}
              >
                {t('classEdit.customProblemsDeleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {addProblemLessonId != null && classData && (() => {
        const lesson = classData.lessons.find((l) => l.id === addProblemLessonId) || null;
        const existingIds = new Set((lesson?.problems || []).map((p) => p.problem_id));
        return (
          <div className="class-edit-modal-overlay" onClick={closeAddProblemModal} role="presentation">
            <div
              className="class-add-problem-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-problem-modal-title"
            >
              <div className="class-add-problem-modal__header">
                <div>
                  <h2 id="add-problem-modal-title" className="class-add-problem-modal__title">
                    {t('classEdit.addProblemModalTitle').replace(
                      '{title}',
                      lesson?.title || t('classEdit.addProblemModalLessonFallback')
                    )}
                  </h2>
                </div>
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-sm"
                  onClick={closeAddProblemModal}
                >
                  {t('classEdit.close')}
                </button>
              </div>

              <div className="class-add-problem-modal__toolbar">
                <div
                  className="class-add-problem-modal__source"
                  role="group"
                  aria-label={t('classEdit.addProblemFilterSource')}
                >
                  <button
                    type="button"
                    className={`class-add-problem-modal__source-btn${problemsSource === 'catalog' ? ' is-active' : ''}`}
                    onClick={() => {
                      setProblemsPage(1);
                      setProblemsSource('catalog');
                    }}
                  >
                    {t('classEdit.addProblemSourceCatalog')}
                  </button>
                  <button
                    type="button"
                    className={`class-add-problem-modal__source-btn${problemsSource === 'custom' ? ' is-active' : ''}`}
                    onClick={() => {
                      setProblemsPage(1);
                      setProblemsSource('custom');
                    }}
                  >
                    {t('classEdit.addProblemSourceCustom')}
                  </button>
                </div>
                <label className="class-add-problem-modal__search">
                  <FaSearch aria-hidden />
                  <input
                    type="search"
                    value={problemsSearch}
                    onChange={(e) => {
                      setProblemsPage(1);
                      setProblemsSearch(e.target.value);
                    }}
                    placeholder={t('classEdit.searchProblemPlaceholder')}
                    autoFocus
                  />
                </label>
                <div className="class-add-problem-modal__filters">
                  <select
                    className="class-edit-input"
                    value={problemsTopic}
                    onChange={(e) => {
                      setProblemsPage(1);
                      setProblemsTopic(e.target.value);
                    }}
                    aria-label={t('classEdit.addProblemFilterTopic')}
                  >
                    <option value="All">{t('classEdit.addProblemAllTopics')}</option>
                    {problemTopics.map((topic) => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>
                  <select
                    className="class-edit-input"
                    value={problemsDifficulty}
                    onChange={(e) => {
                      setProblemsPage(1);
                      setProblemsDifficulty(e.target.value);
                    }}
                    aria-label={t('classEdit.addProblemFilterDifficulty')}
                  >
                    <option value="All">{t('classEdit.addProblemAllDifficulties')}</option>
                    <option value="Easy">{t('classEdit.diffEasy')}</option>
                    <option value="Medium">{t('classEdit.diffMedium')}</option>
                    <option value="Hard">{t('classEdit.diffHard')}</option>
                  </select>
                </div>
              </div>

              <div className="class-add-problem-modal__list-wrap">
                {problemsSearchLoading && (
                  <div className="class-edit-loading-sm">{t('classEdit.searching')}</div>
                )}
                {!problemsSearchLoading && problemsOptions.length === 0 && (
                  <p className="class-edit-invite-hint">
                    {problemsSource === 'custom'
                      ? t('classEdit.addProblemEmptyCustom')
                      : t('classEdit.addProblemEmpty')}
                  </p>
                )}
                <ul className="class-add-problem-modal__list">
                  {problemsOptions.map((p) => {
                    const already = existingIds.has(p.id);
                    return (
                      <li key={p.id} className={`class-add-problem-modal__item${already ? ' is-added' : ''}`}>
                        <div className="class-add-problem-modal__item-main">
                          <span className="class-add-problem-modal__item-title">{p.title}</span>
                          {p.topic ? (
                            <span className="class-add-problem-modal__item-topic">{p.topic}</span>
                          ) : null}
                        </div>
                        <div className="class-add-problem-modal__item-side">
                          <span
                            className="class-edit-difficulty"
                            style={{ color: getDifficultyColor(p.difficulty) }}
                          >
                            {getDifficultyLabel(p.difficulty)}
                          </span>
                          {already ? (
                            <span className="class-add-problem-modal__badge">
                              {t('classEdit.addProblemAlreadyAdded')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="class-edit-btn class-edit-btn-primary class-edit-btn-sm class-add-problem-modal__add-btn"
                              disabled={addingProblemId === p.id}
                              onClick={() => handleAddProblemToLesson(addProblemLessonId, p.id)}
                            >
                              {addingProblemId === p.id ? '…' : t('classEdit.addProblemAction')}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {problemsTotalPages > 1 && (
                <div className="class-add-problem-modal__pager">
                  <button
                    type="button"
                    className="class-edit-btn class-edit-btn-sm"
                    disabled={problemsPage <= 1 || problemsSearchLoading}
                    onClick={() => setProblemsPage((p) => Math.max(1, p - 1))}
                  >
                    ‹
                  </button>
                  <span>
                    {problemsPage} / {problemsTotalPages}
                  </span>
                  <button
                    type="button"
                    className="class-edit-btn class-edit-btn-sm"
                    disabled={problemsPage >= problemsTotalPages || problemsSearchLoading}
                    onClick={() => setProblemsPage((p) => Math.min(problemsTotalPages, p + 1))}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {analyticsModalSubmission && (
        <div className="class-analytics-modal-overlay" onClick={closeAnalyticsCodeModal} role="presentation">
          <div className="class-analytics-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="class-analytics-modal-header">
              <h2>{t('classEdit.codeSolution')}</h2>
              <button type="button" className="class-edit-btn class-edit-btn-sm" onClick={closeAnalyticsCodeModal}>
                {t('classEdit.modalClose')}
              </button>
            </div>
            <div className="class-analytics-modal-select-row">
              <label htmlFor="class-analytics-submission-select">{t('classEdit.submissionSelectLabel')}</label>
              <select
                id="class-analytics-submission-select"
                className="class-edit-input class-analytics-modal-select"
                value={
                  analyticsModalSubmission.id != null
                    ? String(analyticsModalSubmission.id)
                    : analyticsModalSubmission.submitted_at
                }
                disabled={analyticsModalHistoryLoading || analyticsModalHistory.length <= 1}
                onChange={(e) => {
                  const value = e.target.value;
                  const next =
                    analyticsModalHistory.find((item) =>
                      item.id != null ? String(item.id) === value : item.submitted_at === value,
                    ) || null;
                  if (next) setAnalyticsModalSubmission(next);
                }}
              >
                {analyticsModalHistory.map((item, index) => {
                  const key = item.id != null ? String(item.id) : `${item.submitted_at}-${index}`;
                  const value = item.id != null ? String(item.id) : item.submitted_at;
                  const when = new Date(item.submitted_at).toLocaleString(
                    uiLang === 'ru' ? 'ru-RU' : undefined,
                  );
                  return (
                    <option key={key} value={value}>
                      {`#${analyticsModalHistory.length - index} · ${when} · ${item.language} · ${submissionStatusLabel(item.status)}`}
                    </option>
                  );
                })}
              </select>
              {analyticsModalHistoryLoading ? (
                <span className="class-analytics-modal-select-hint">{t('classEdit.submissionSelectLoading')}</span>
              ) : analyticsModalHistory.length > 1 ? (
                <span className="class-analytics-modal-select-hint">
                  {t('classEdit.submissionSelectCount').replace('{count}', String(analyticsModalHistory.length))}
                </span>
              ) : null}
            </div>
            <div className="class-analytics-modal-meta">
              <span><strong>{t('classEdit.languageLabel')}</strong> {analyticsModalSubmission.language}</span>
              <span><strong>{t('classEdit.timeLabel')}</strong> {analyticsModalSubmission.execution_time ?? '—'} {t('classEdit.timeSec')}</span>
              <span><strong>{t('classEdit.submittedLabel')}</strong> {new Date(analyticsModalSubmission.submitted_at).toLocaleString()}</span>
              <span><strong>{t('classEdit.statusLabel')}</strong> {submissionStatusLabel(analyticsModalSubmission.status)}</span>
            </div>
            <pre className="class-analytics-code-block">
{analyticsModalSubmission.code}
            </pre>
            {analyticsModalSubmission.id != null && (
              <div className="class-analytics-feedback">
                <div className="class-analytics-feedback-title">{t('classEdit.feedbackTitle')}</div>
                {analyticsCommentsLoading ? (
                  <div className="class-analytics-feedback-hint">{t('classEdit.feedbackLoading')}</div>
                ) : analyticsComments.length > 0 ? (
                  <ul className="class-analytics-feedback-list">
                    {analyticsComments.map((item) => (
                      <li
                        key={item.id}
                        className={`class-analytics-feedback-item is-${item.author_role === 'student' ? 'student' : 'teacher'}`}
                      >
                        <span className="class-analytics-feedback-item-date">
                          {item.author_role === 'student'
                            ? t('classEdit.feedbackAuthorStudent')
                            : t('classEdit.feedbackAuthorTeacher')}
                          {' · '}
                          {new Date(item.createdAt).toLocaleString(uiLang === 'ru' ? 'ru-RU' : undefined)}
                          {item.author_role !== 'student' ? (
                            <>
                              {' · '}
                              {item.read_at
                                ? t('classEdit.feedbackRead')
                                : t('classEdit.feedbackUnread')}
                            </>
                          ) : null}
                        </span>
                        <p className="class-analytics-feedback-item-body">{item.body}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <textarea
                  className="class-edit-input class-analytics-feedback-input"
                  rows={3}
                  maxLength={2000}
                  value={analyticsCommentDraft}
                  onChange={(e) => {
                    setAnalyticsCommentDraft(e.target.value);
                    if (analyticsCommentSent) setAnalyticsCommentSent(false);
                    if (analyticsCommentError) setAnalyticsCommentError(null);
                  }}
                  placeholder={t('classEdit.feedbackPlaceholder')}
                  disabled={analyticsCommentSending}
                />
                <div className="class-analytics-feedback-actions">
                  {analyticsCommentError ? (
                    <span className="class-analytics-feedback-status is-error">{analyticsCommentError}</span>
                  ) : analyticsCommentSent ? (
                    <span className="class-analytics-feedback-status is-ok">{t('classEdit.feedbackSent')}</span>
                  ) : (
                    <span className="class-analytics-feedback-hint">{t('classEdit.feedbackHint')}</span>
                  )}
                  <button
                    type="button"
                    className="class-edit-btn class-edit-btn-primary class-edit-btn-sm"
                    onClick={() => void sendAnalyticsComment()}
                    disabled={analyticsCommentSending || !analyticsCommentDraft.trim()}
                  >
                    {analyticsCommentSending ? t('classEdit.feedbackSending') : t('classEdit.feedbackSend')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassEditPage;
